import {
  escapePlaintextHtml,
  sanitizeRichHtml,
  sameToolMutationIdentity,
  toolMutationIdentity,
  toolOperationStorageKey,
} from "./chatFrontendGuards.js?v=1.0.2";
import {
  humanizeReviewWarnings,
  PARTIAL_REVIEW_WARNING,
} from "./reviewUserCopy.js?v=1.0.0";
import {
  createToolResultAccessError,
  toolResultAccessFeedback,
} from "./toolResultAccessFeedback.js?v=1.0.0";
import { createReviewApplySnapshot } from "./reviewFollowup.js?v=1.0.1";

const MAX_CONTEXT_SELECTION_SOURCES = 20;
const DEFAULT_MUTATION_TIMEOUT_MS = 30000;
const MUTATION_RECONCILIATION_TIMEOUT_MS = 10000;
const MIN_NORMALIZED_SOURCE_MATCH_LENGTH = 8;

export function findingApplicationStatus(finding) {
  const disposition = String(finding && finding.disposition || 'open').toLowerCase();
  if (disposition !== 'accepted') return 'not_applicable';
  const recordedStatus = String(
    finding && finding.application_status || '',
  ).toLowerCase();

  const currentVersion = Number(
    finding && (finding.finding_state_version ?? finding.state_version),
  );
  const normalizedCurrentVersion = Number.isInteger(currentVersion) && currentVersion >= 1
    ? currentVersion
    : 1;
  const appliedVersion = Number(finding && finding.applied_finding_state_version);
  if (Number.isInteger(appliedVersion) && appliedVersion > 0) {
    if (appliedVersion < normalizedCurrentVersion) return 'pending';
    return recordedStatus === 'partially_applied'
      ? 'partially_applied'
      : 'applied';
  }
  return ['applied', 'partially_applied'].includes(recordedStatus)
    ? recordedStatus
    : 'pending';
}

export function findingHasAppliedChanges(finding) {
  const recordedStatus = String(
    finding && finding.application_status || '',
  ).toLowerCase();
  return appliedFindingHistoryVersion(finding) > 0
    || ['applied', 'partially_applied'].includes(recordedStatus);
}

export function findingHasPartialApplicationHistory(finding) {
  const historicalStatus = String(
    finding && (
      finding.last_application_status
      || finding.recorded_application_status
      || finding.application_status
    ) || '',
  ).toLowerCase();
  return historicalStatus === 'partially_applied'
    && findingHasAppliedChanges(finding);
}

export function appliedFindingHistoryVersion(finding) {
  const appliedVersion = Number(finding && finding.applied_finding_state_version);
  return Number.isInteger(appliedVersion) && appliedVersion > 0 ? appliedVersion : 0;
}

export function reviewFindingCompletion(finding, options = {}) {
  const disposition = String(finding && finding.disposition || 'open').toLowerCase();
  if (options.usesConsiderActions && disposition === 'accepted') {
    return { kind: 'considered', label: 'Hallazgo considerado' };
  }
  if (!options.isEditable) return null;
  const applicationStatus = findingApplicationStatus(finding);
  if (applicationStatus === 'partially_applied') {
    return {
      kind: 'partially-applied',
      label: 'Aplicado parcialmente · completa los marcadores',
    };
  }
  if (applicationStatus === 'applied') {
    return { kind: 'applied', label: 'Aplicado al documento' };
  }
  if (disposition === 'accepted' && findingHasPartialApplicationHistory(finding)) {
    return {
      kind: 'partially-applied-previously',
      label: 'Aplicado parcialmente antes · pendiente de volver a aplicar',
    };
  }
  if (disposition === 'accepted' && findingHasAppliedChanges(finding)) {
    return {
      kind: 'applied-previously',
      label: 'Aplicado anteriormente · pendiente de volver a aplicar',
    };
  }
  if (disposition !== 'accepted' && findingHasPartialApplicationHistory(finding)) {
    return {
      kind: 'partially-applied-previously',
      label: 'Aplicado parcialmente anteriormente · completa los marcadores',
    };
  }
  if (disposition !== 'accepted' && findingHasAppliedChanges(finding)) {
    return { kind: 'applied-previously', label: 'Aplicado anteriormente' };
  }
  return null;
}

export function appliedFindingUnapprovalCopy(finding, disposition) {
  const destination = {
    open: {
      outcome: 'El hallazgo volverá a quedar pendiente.',
      actionLabel: 'Retirar aprobación',
    },
    dismissed: {
      outcome: 'El hallazgo quedará descartado.',
      actionLabel: 'Retirar y descartar',
    },
    intentional: {
      outcome: 'El hallazgo se marcará como intencional.',
      actionLabel: 'Retirar y mantener como está',
    },
  }[String(disposition || '').toLowerCase()] || {
    outcome: 'El hallazgo dejará de estar aprobado.',
    actionLabel: 'Retirar aprobación',
  };
  const currentApplicationStatus = findingApplicationStatus(finding);
  const partialApplicationHistory = findingHasPartialApplicationHistory(finding);
  const applicationWarning = partialApplicationHistory
    ? currentApplicationStatus === 'partially_applied'
      ? 'Esta recomendación se aplicó parcialmente al documento con marcadores pendientes.'
      : 'Esta recomendación se aplicó parcialmente con una aprobación anterior y todavía tiene marcadores pendientes.'
    : currentApplicationStatus === 'applied'
      ? 'Esta recomendación ya se aplicó al documento.'
    : 'Este hallazgo ya se aplicó con una aprobación anterior; la aprobación actual aún no se ha aplicado.';
  return {
    ...destination,
    message: `${applicationWarning} Retirar la aprobación no deshará el cambio ni modificará la versión creada. ${destination.outcome} Si quieres cambiar lo que ya se aplicó, pídele directamente a la IA que modifique el documento en un mensaje nuevo.`,
  };
}

export function activeReviewMaterialSignature(review) {
  const snapshot = createReviewApplySnapshot(review);
  if (!snapshot || !snapshot.reviewId || !snapshot.materialState) return '';
  return `${snapshot.reviewId}|${snapshot.materialState}`;
}

async function confirmAppliedFindingUnapproval(finding, disposition) {
  const copy = appliedFindingUnapprovalCopy(finding, disposition);
  const requestDecision = globalThis && globalThis.requestChatFileDecision;
  if (typeof requestDecision === 'function') {
    try {
      const decision = await requestDecision({
        testId: 'review-unapproval-confirmation',
        eyebrow: 'Recomendación ya aplicada',
        title: '¿Retirar la aprobación de este hallazgo?',
        message: copy.message,
        iconClass: 'anticon-warning',
        showFiles: false,
        primary: {
          label: 'Conservar aprobación',
          iconClass: 'anticon-check',
          testId: 'review-unapproval-keep',
          value: 'keep',
        },
        secondary: {
          label: copy.actionLabel,
          testId: 'review-unapproval-confirm',
          value: 'remove',
        },
        cancelValue: 'keep',
      });
      return decision === 'remove';
    } catch (error) {
      // Fall through to the browser confirmation if the shared dialog is unavailable.
    }
  }
  return typeof globalThis.confirm === 'function'
    ? globalThis.confirm(`¿Retirar la aprobación de este hallazgo?\n\n${copy.message}`)
    : false;
}

function normalizeSourcePassage(value) {
  const source = String(value == null ? '' : value);
  let text = '';
  const sourceIndexes = [];
  let pendingSeparatorIndex = -1;

  for (let sourceIndex = 0; sourceIndex < source.length;) {
    const sourceCharacter = String.fromCodePoint(source.codePointAt(sourceIndex));
    const foldedCharacters = sourceCharacter
      .normalize('NFKD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLocaleLowerCase('es');

    for (const foldedCharacter of foldedCharacters) {
      if (/^[\p{L}\p{N}]$/u.test(foldedCharacter)) {
        if (pendingSeparatorIndex >= 0 && text) {
          text += ' ';
          sourceIndexes.push(pendingSeparatorIndex);
        }
        pendingSeparatorIndex = -1;
        text += foldedCharacter;
        sourceIndexes.push(sourceIndex);
      } else if (text && pendingSeparatorIndex < 0) {
        pendingSeparatorIndex = sourceIndex;
      }
    }
    sourceIndex += sourceCharacter.length;
  }

  return { text, sourceIndexes };
}

export function findSourcePassageMatch(sourceValue, passageValue) {
  const source = String(sourceValue == null ? '' : sourceValue);
  const passage = String(passageValue == null ? '' : passageValue).trim();
  if (!source || !passage) return null;

  const exactStart = source.indexOf(passage);
  if (exactStart >= 0) {
    return { start: exactStart, end: exactStart + passage.length, kind: 'exact' };
  }

  const normalizedSource = normalizeSourcePassage(source);
  const normalizedPassage = normalizeSourcePassage(passage).text;
  if (
    normalizedPassage.replace(/\s/g, '').length < MIN_NORMALIZED_SOURCE_MATCH_LENGTH
    || !normalizedSource.text
  ) return null;

  const normalizedStart = normalizedSource.text.indexOf(normalizedPassage);
  if (normalizedStart < 0) return null;
  const normalizedEnd = normalizedStart + normalizedPassage.length - 1;
  const start = normalizedSource.sourceIndexes[normalizedStart];
  const lastSourceIndex = normalizedSource.sourceIndexes[normalizedEnd];
  if (!Number.isInteger(start) || !Number.isInteger(lastSourceIndex)) return null;
  const lastCharacter = String.fromCodePoint(source.codePointAt(lastSourceIndex));
  return {
    start,
    end: lastSourceIndex + lastCharacter.length,
    kind: 'normalized',
  };
}

export default {
  props: {
    receipt: { type: Object, default: null },
    chatUuid: { type: String, default: '' },
    inlinePassages: { type: Array, default: null },
    reviewFocusMode: {
      type: String,
      default: 'all',
      validator: value => ['all', 'pending'].includes(String(value || 'all')),
    },
    activeReview: { type: Object, default: null },
  },
  data() {
    return {
      loading: false,
      error: '',
      loadRetryable: false,
      loadFailure: null,
      loadRetryVersion: null,
      projection: null,
      versions: [],
      anchorEvidenceById: {},
      openAnchorIds: {},
      loadingAnchorIds: {},
      anchorLoadErrors: {},
      pendingExactSourceKey: '',
      dispositionReasonDrafts: {},
      mutationBusy: false,
      reviewDecisionConfirmationPending: false,
      mutationController: null,
      mutationTimeoutId: null,
      contextQuery: '',
      contextSources: [],
      selectedSourceIds: [],
      sourceRequestItemIds: {},
      maxContextSelectionSources: MAX_CONTEXT_SELECTION_SOURCES,
      contextCursor: null,
      contextResolutionEpoch: 0,
      loadGeneration: 0,
      loadController: null,
      contextSearchGeneration: 0,
      contextSearchController: null,
      contextLoading: false,
      operationKeys: {},
      uploadingContextDocument: false,
      contextUploadMessage: '',
      activeFindingMutationId: '',
      activeReviewViewportAnchor: null,
      reviewFilterMode: this.reviewFocusMode === 'pending' ? 'pending' : 'all',
      reviewPendingTitlesReady: false,
      reviewDecisionReceipts: {},
      reviewApplyConfirmation: null,
      handledReviewApplySnapshotKey: '',
      savedFindingId: '',
      copiedPassageKey: '',
      copyPassageErrorKey: '',
      copyPassageTimer: null,
      receiptGeneration: 0,
      // A receipt can intentionally pin an older immutable artifact version.
      // Once that version is restored, keep the workspace on the newly-created
      // current version instead of re-applying the receipt's stale pin.
      artifactVersionOverride: null,
    };
  },
  computed: {
    isOpen() { return !!this.receipt; },
    resultRef() {
      return this.receipt && this.receipt.result_ref && typeof this.receipt.result_ref === 'object'
        ? this.receipt.result_ref
        : null;
    },
    isInlineSourceRead() {
      return Boolean(
        this.receipt
        && this.receipt.tool_name === 'leer_fuente'
        && Array.isArray(this.inlinePassages)
      );
    },
    isSourceWorkspace() {
      return this.isInlineSourceRead || Boolean(
        this.resultRef && this.resultRef.type === 'anchor_set'
      );
    },
    title() {
      if (this.isSourceWorkspace) {
        return 'Pasajes de fuentes consultadas';
      }
      const ui = this.receipt && this.receipt.ui_payload;
      return String((ui && ui.title) || 'Detalle del documento');
    },
    receiptSummary() {
      const ui = this.receipt && this.receipt.ui_payload;
      return String(ui && ui.summary || '').trim();
    },
    workspaceTypeClass() {
      if (this.isContextRequest) return 'tool-workspace__panel--context';
      if (this.resultRef && this.resultRef.type === 'review') return 'tool-workspace__panel--review';
      if (this.isContinuation) return 'tool-workspace__panel--continuation';
      if (this.isSourceWorkspace) return 'tool-workspace__panel--sources';
      return 'tool-workspace__panel--document';
    },
    workspaceEyebrow() {
      if (this.isContextRequest) return 'Acción requerida';
      if (this.resultRef && this.resultRef.type === 'review') {
        return this.reviewIsInstitutional ? 'Revisión institucional guardada' : 'Revisión guardada';
      }
      if (this.isContinuation) return 'Estado de la respuesta';
      if (this.isSourceWorkspace) return 'Evidencia de la respuesta';
      return 'Documento guardado';
    },
    workspaceIntro() {
      if (this.isContextRequest) return 'Añade la información solicitada para que el Abogado Virtual pueda continuar.';
      if (this.resultRef && this.resultRef.type === 'review') {
        return this.reviewIsInstitutional
          ? 'Evalúa las posibles incidencias y decide cuáles conservar para tu análisis.'
          : 'Revisa los hallazgos y decide cómo tratar cada uno.';
      }
      if (this.isSourceWorkspace) {
        return this.isInlineSourceRead
          ? 'Estos son los fragmentos exactos que LEXIUS consultó para preparar el resumen.'
          : 'Estos son los fragmentos exactos que LEXIUS consultó para preparar su respuesta.';
      }
      return '';
    },
    workspaceIconClass() {
      if (this.isContextRequest) return 'anticon anticon-file-search';
      if (this.resultRef && this.resultRef.type === 'review') return 'anticon anticon-audit';
      if (this.isContinuation) return 'anticon anticon-sync';
      if (this.isSourceWorkspace) return 'anticon anticon-book';
      return 'anticon anticon-file-text';
    },
    requestedArtifactVersion() {
      const override = this.artifactVersionOverride;
      const version = Number(
        override === null ? (this.receipt && this.receipt.requested_artifact_version) : override,
      );
      return Number.isInteger(version) && version > 0 ? version : null;
    },
    renderedArtifact() {
      if (this.artifactProvenanceUnavailable) return '';
      return this.renderMarkdown(this.projection && this.projection.content_markdown);
    },
    artifactProvenanceUnavailable() {
      return !!(
        this.resultRef
        && this.resultRef.type === 'document'
        && this.projection
        && this.projection.available === false
      );
    },
    findings() {
      return this.projection && Array.isArray(this.projection.findings)
        ? this.projection.findings
        : [];
    },
    reviewSubjectKind() {
      const value = String(this.projection && this.projection.review_subject_kind || '')
        .trim()
        .toLocaleLowerCase('es-ES');
      const known = [
        'user_upload',
        'corporate_upload',
        'generated_document',
        'attached_content',
        'legislation',
        'jurisprudence',
        'institutional',
      ];
      if (known.includes(value)) return value;
      return String(this.projection && this.projection.review_profile || '').toLowerCase() === 'institutional'
        ? 'institutional'
        : 'attached_content';
    },
    reviewIsEditable() {
      const profile = String(this.projection && this.projection.review_profile || '')
        .trim()
        .toLocaleLowerCase('es-ES');
      if (['institutional', 'read_only'].includes(profile) || [
        'legislation',
        'jurisprudence',
        'institutional',
      ].includes(this.reviewSubjectKind)) {
        return false;
      }
      if (this.projection && typeof this.projection.review_subject_editable === 'boolean') {
        return this.projection.review_subject_editable;
      }
      return true;
    },
    reviewIsInstitutional() {
      const profile = String(this.projection && this.projection.review_profile || '')
        .trim()
        .toLocaleLowerCase('es-ES');
      return profile === 'institutional' || [
        'legislation',
        'jurisprudence',
        'institutional',
      ].includes(this.reviewSubjectKind);
    },
    reviewUsesConsiderActions() {
      return !this.reviewIsEditable;
    },
    reviewSubjectLabel() {
      const labels = {
        user_upload: 'Archivo personal',
        corporate_upload: 'Archivo corporativo',
        generated_document: 'Documento generado',
        attached_content: 'Contenido adjunto',
        legislation: 'Legislación de LEXIUS',
        jurisprudence: 'Jurisprudencia de LEXIUS',
        institutional: 'Contenido institucional de LEXIUS',
      };
      return labels[this.reviewSubjectKind] || labels.attached_content;
    },
    reviewAllowedDispositions() {
      const available = this.projection && this.projection.allowed_dispositions;
      const allowed = ['accepted', 'intentional', 'dismissed', 'open'];
      if (Array.isArray(available)) {
        const normalized = [...new Set(available.map(value => String(value || '').toLowerCase()))]
          .filter(value => allowed.includes(value));
        if (normalized.length) return normalized;
      }
      return this.reviewUsesConsiderActions
        ? ['accepted', 'dismissed', 'open']
        : allowed;
    },
    reviewFindingActions() {
      if (this.reviewUsesConsiderActions) {
        return [
          {
            disposition: 'accepted',
            mark: '✓',
            label: 'Considerar hallazgo',
            detail: 'Mantenerlo señalado en esta revisión',
          },
          {
            disposition: 'dismissed',
            mark: '×',
            label: 'Descartar hallazgo',
            detail: 'No considerarlo en esta revisión',
          },
        ].filter(action => this.reviewAllowedDispositions.includes(action.disposition));
      }
      return [
        {
          disposition: 'accepted',
          mark: '✓',
          label: 'Aprobar recomendación',
          detail: 'Marcar para una edición posterior',
        },
        {
          disposition: 'intentional',
          mark: '＝',
          label: 'Mantener como está',
          detail: 'La redacción es intencional',
        },
        {
          disposition: 'dismissed',
          mark: '×',
          label: 'Descartar hallazgo',
          detail: 'No considerarlo en esta revisión',
        },
      ].filter(action => this.reviewAllowedDispositions.includes(action.disposition));
    },
    reviewInstitutionalNotice() {
      if (!this.reviewIsInstitutional) return '';
      return String(this.projection && this.projection.review_subject_notice || '').trim()
        || 'Los hallazgos son señales para verificar, no afirmaciones de que la fuente oficial contenga un error. Contrástalos con la versión oficial vigente y su contexto completo.';
    },
    reviewNotePlaceholder() {
      return this.reviewIsInstitutional
        ? 'Ej. Verificar esta posible incidencia en la publicación oficial vigente.'
        : 'Ej. La cláusula fue pactada expresamente por las partes.';
    },
    reviewFindingsHeading() {
      return this.reviewIsInstitutional ? 'Hallazgos para evaluar' : 'Hallazgos para decidir';
    },
    reviewSeverityPrefix() {
      return this.reviewIsInstitutional ? 'Prioridad' : 'Riesgo';
    },
    reviewIsPartial() {
      return !!(this.projection && (
        String(this.projection.coverage || '').toLowerCase() === 'partial'
        || this.projection.unsupported_focus === true
      ));
    },
    reviewWarnings() {
      const warnings = this.projection && this.projection.warnings;
      return humanizeReviewWarnings(
        Array.isArray(warnings) ? warnings : [],
      );
    },
    reviewWarningText() {
      if (this.reviewWarnings.length) return this.reviewWarnings.join(' ');
      if (this.projection && this.projection.unsupported_focus === true) {
        return 'El enfoque solicitado no pudo revisarse por completo.';
      }
      return this.reviewIsPartial ? PARTIAL_REVIEW_WARNING : '';
    },
    reviewCheckedCategoryLabels() {
      const reviewedAspects = this.projection && this.projection.reviewed_aspects;
      const categories = Array.isArray(reviewedAspects) && reviewedAspects.length
        ? reviewedAspects
        : (this.projection && this.projection.checked_categories);
      if (!Array.isArray(categories)) return [];
      const labels = {
        legalidad_y_normas_imperativas: 'normas imperativas y legalidad',
        afirmaciones_juridicas_y_citas: 'afirmaciones y citas jurídicas',
        derechos_y_obligaciones: 'derechos y obligaciones',
        plazos_y_prescripcion: 'plazos y prescripción',
        competencia_y_jurisdiccion: 'competencia y jurisdicción',
        ejecutabilidad_y_remedios: 'ejecutabilidad y remedios',
        formalidades: 'formalidades exigibles',
        coherencia_interna: 'coherencia interna',
        ambiguedad: 'ambigüedades relevantes',
        referencias_cruzadas_y_definiciones: 'definiciones y referencias cruzadas',
        fechas_importes_y_calculos: 'fechas, importes y cálculos',
        riesgos_practicos: 'riesgos prácticos',
        placeholder: 'campos pendientes',
        numbering_gap: 'secuencia de numeración',
        missing_annex: 'anexos citados',
        exact_duplicate: 'bloques duplicados',
        total_mismatch: 'totales aritméticos',
      };
      return categories
        .filter(item => typeof item === 'string' && item.trim())
        .slice(0, 20)
        .map(item => labels[item] || item.replace(/_/g, ' '));
    },
    reviewAnalysisSummary() {
      return String(this.projection && this.projection.analysis_summary || '').trim();
    },
    reviewLegalFramework() {
      const values = this.projection && this.projection.legal_framework;
      return Array.isArray(values)
        ? values.filter(item => item && typeof item === 'object')
        : [];
    },
    reviewedDocumentEvidence() {
      if (!this.projection) return [];
      const values = this.projection.reviewed_document_evidence
        || this.projection.document_review_evidence
        || this.projection.document_evidence;
      return Array.isArray(values)
        ? values.filter(item => item && typeof item === 'object')
        : [];
    },
    reviewAuthorityCount() {
      const explicit = Number(
        this.projection && (
          this.projection.legal_authority_count
          ?? this.projection.legal_framework_count
          ?? this.projection.total_legal_sources
          ?? this.projection.authority_count
        ),
      );
      return Number.isInteger(explicit) && explicit >= 0
        ? explicit
        : this.reviewLegalFramework.length;
    },
    reviewProvenanceItems() {
      if (!this.projection) return [];
      const items = [];
      const append = (label, value) => {
        const normalized = String(value == null ? '' : value).trim();
        if (normalized) items.push({ label, value: normalized });
      };
      append(
        'Documento',
        this.projection.document_title
          || this.projection.source_title
          || this.projection.source_handle,
      );
      append('Tipo de contenido', this.reviewSubjectLabel);
      append(
        'Enfoque solicitado',
        this.projection.focus
          || this.projection.focus_text
          || this.projection.requested_focus
          || this.projection.enfoque,
      );
      append(
        'Fecha de revisión',
        this.formatReviewDate(this.projection.reviewed_at || this.projection.created_at),
      );
      append('Fuentes jurídicas', this.reviewAuthorityCount);
      return items;
    },
    reviewOverviewProvenanceItems() {
      const overviewLabels = new Set(['Documento', 'Tipo de contenido', 'Enfoque solicitado', 'Fecha de revisión']);
      return this.reviewProvenanceItems.filter(item => overviewLabels.has(item.label));
    },
    reviewHighFindingCount() {
      return this.findings.filter(finding => String(finding && finding.severity || '').toLowerCase() === 'high').length;
    },
    reviewPendingFindingCount() {
      return this.findings.filter(finding => String(finding && finding.disposition || 'open').toLowerCase() === 'open').length;
    },
    reviewPendingFindings() {
      return this.findings.filter(finding => (
        String(finding && finding.disposition || 'open').toLowerCase() === 'open'
      ));
    },
    reviewIsPendingFilter() {
      return this.reviewFilterMode === 'pending';
    },
    visibleReviewFindings() {
      if (!this.reviewIsPendingFilter) return this.findings;
      const receipts = this.reviewDecisionReceipts && typeof this.reviewDecisionReceipts === 'object'
        ? this.reviewDecisionReceipts
        : {};
      return this.findings.filter((finding) => {
        const disposition = String(finding && finding.disposition || 'open').toLowerCase();
        if (disposition === 'open') return true;
        const findingId = String(finding && finding.finding_id || '');
        const receipt = findingId && receipts[findingId];
        const findingStateVersion = Number(
          finding && (finding.finding_state_version ?? finding.state_version),
        );
        return !!(
          receipt
          && String(receipt.disposition || '').toLowerCase() === disposition
          && Number(receipt.stateVersion || 1) === (
            Number.isInteger(findingStateVersion) && findingStateVersion >= 1
              ? findingStateVersion
              : 1
          )
        );
      });
    },
    reviewTourFinding() {
      const candidates = this.reviewIsPendingFilter
        ? this.reviewPendingFindings
        : this.visibleReviewFindings;
      return candidates.find(finding => (
        finding
        && finding.evidence
        && Array.isArray(finding.evidence.legal_bases)
        && finding.evidence.legal_bases.length
      )) || candidates[0] || null;
    },
    visibleOpenReviewFindings() {
      return this.visibleReviewFindings.filter(finding => (
        String(finding && finding.disposition || 'open').toLowerCase() === 'open'
      ));
    },
    reviewAcceptedFindings() {
      return this.findings.filter(finding => (
        String(finding && finding.disposition || '').toLowerCase() === 'accepted'
      ));
    },
    activeReviewMatchesWorkspace() {
      if (!this.activeReview || typeof this.activeReview !== 'object') return false;
      const activeReviewId = String(this.activeReview.review_id || this.activeReview.id || '');
      const workspaceReviewId = String(this.resultRef && this.resultRef.id || '');
      return !!workspaceReviewId && activeReviewId === workspaceReviewId;
    },
    activeReviewApplySnapshot() {
      if (!this.activeReviewMatchesWorkspace) return null;
      return createReviewApplySnapshot(this.activeReview);
    },
    reviewApprovedPendingCount() {
      const count = Number(this.activeReviewApplySnapshot && this.activeReviewApplySnapshot.approvedCount);
      return Number.isInteger(count) && count >= 0 ? count : 0;
    },
    reviewCanApplyApproved() {
      const snapshot = this.activeReviewApplySnapshot;
      return Boolean(
        this.reviewIsEditable
        && snapshot
        && snapshot.reviewId === String(this.resultRef && this.resultRef.id || '')
        && snapshot.materialState
        && snapshot.approvedCount > 0
        && snapshot.approvedFindingIds.length === snapshot.approvedCount
        && snapshot.approvedFindingIds.every(identifier => !!identifier)
        && new Set(snapshot.approvedFindingIds).size === snapshot.approvedFindingIds.length
      );
    },
    reviewCanShowBulkActions() {
      return Boolean(
        this.reviewIsEditable
        && this.visibleOpenReviewFindings.length
        && this.reviewPendingTitlesReady
      );
    },
    reviewStats() {
      return [
        {
          label: 'Hallazgos',
          value: this.findings.length,
          tone: this.findings.length ? 'attention' : 'positive',
        },
        {
          label: 'Prioridad alta',
          value: this.reviewHighFindingCount,
          tone: this.reviewHighFindingCount ? 'risk' : 'neutral',
        },
        {
          label: 'Fuentes jurídicas',
          value: this.reviewAuthorityCount,
          tone: 'verified',
        },
      ];
    },
    anchorPassages() {
      const passages = this.projection && Array.isArray(this.projection.passages)
        ? this.projection.passages
        : [];
      if (passages.length) {
        return passages.filter(item => item && typeof item === 'object' && typeof item.quote === 'string');
      }
      if (this.projection && typeof this.projection.quote === 'string') {
        return [{
          anchor_id: this.resultRef && this.resultRef.id,
          source_title: '',
          quote: this.projection.quote,
          revision: this.projection.revision || '',
        }];
      }
      return [];
    },
    sourceWarnings() {
      const ui = this.receipt && this.receipt.ui_payload;
      const warnings = ui && ui.warnings;
      return Array.isArray(warnings)
        ? warnings.filter(item => typeof item === 'string' && item.trim()).slice(0, 20)
        : [];
    },
    sourceAvailableCount() {
      const explicit = Number(this.projection && this.projection.sources_authorized);
      if (Number.isInteger(explicit) && explicit >= 0) return explicit;
      return new Set(this.anchorPassages
        .map(passage => String(passage.source_title || '').trim())
        .filter(Boolean)).size;
    },
    sourceOverviewStats() {
      const passages = this.anchorPassages.length;
      return [
        {
          value: passages,
          label: passages === 1 ? 'pasaje' : 'pasajes',
          tone: passages ? 'verified' : 'neutral',
        },
        {
          value: this.sourceAvailableCount,
          label: this.sourceAvailableCount === 1 ? 'fuente disponible' : 'fuentes disponibles',
          tone: 'verified',
        },
      ];
    },
    requestedDocuments() {
      const ui = this.receipt && this.receipt.ui_payload;
      const values = ui && (ui.requested_documents || ui.requests);
      if (!Array.isArray(values)) return [];
      return values.map((item) => {
        if (typeof item === 'string') {
          return {
            requestItemId: '',
            label: item.slice(0, 300),
            detail: '',
            priority: 'recomendado',
            acceptedTypes: ['documento', 'imagen', 'audio', 'video'],
          };
        }
        if (!item || typeof item !== 'object') return null;
        const label = String(
          item.label
          || item.title
          || item.documento
          || item.name
          || item.tipo
          || item.description
          || item.descripcion
          || '',
        ).slice(0, 300);
        const rawDetail = String(item.description || item.descripcion || item.detail || '').slice(0, 500);
        const detail = rawDetail.trim().toLocaleLowerCase('es-ES') === label.trim().toLocaleLowerCase('es-ES')
          ? ''
          : rawDetail;
        const rawAcceptedTypes = Array.isArray(item.accepted_types)
          ? item.accepted_types
          : (Array.isArray(item.tipos_aceptados) ? item.tipos_aceptados : ['documento']);
        const acceptedTypes = [...new Set(rawAcceptedTypes.map((value) => {
          const normalized = String(value || '').trim().toLocaleLowerCase('es-ES');
          return ({ document: 'documento', image: 'imagen' })[normalized] || normalized;
        }).filter(value => ['documento', 'imagen', 'audio', 'video'].includes(value)))];
        return label ? {
          requestItemId: String(item.request_item_id || item.id || '').slice(0, 128),
          label,
          detail,
          priority: String(item.priority || item.prioridad || '').toLocaleLowerCase('es-ES') === 'necesario'
            ? 'necesario'
            : 'recomendado',
          acceptedTypes: acceptedTypes.length ? acceptedTypes : ['documento'],
        } : null;
      }).filter(Boolean);
    },
    structuredRequestItems() {
      return this.requestedDocuments.filter(item => item.requestItemId);
    },
    hasNecessaryRequestItems() {
      return this.structuredRequestItems.some(item => item.priority === 'necesario');
    },
    uncoveredNecessaryRequestItems() {
      const covered = new Set(this.selectedSourceIds.map(sourceId => (
        String(this.sourceRequestItemIds[String(sourceId)] || '')
      )).filter(Boolean));
      return this.structuredRequestItems.filter(item => (
        item.priority === 'necesario' && !covered.has(item.requestItemId)
      ));
    },
    selectedSourceSelections() {
      return this.selectedSourceIds.map(sourceId => ({
        source_id: String(sourceId),
        request_item_id: String(this.sourceRequestItemIds[String(sourceId)] || ''),
      }));
    },
    coveredRequestItemIds() {
      return new Set(this.selectedSourceSelections
        .map(selection => selection.request_item_id)
        .filter(Boolean));
    },
    coveredRequestItemsCount() {
      return this.structuredRequestItems.filter(item => this.coveredRequestItemIds.has(item.requestItemId)).length;
    },
    contextSelectionStatus() {
      const selected = this.selectedSourceIds.length;
      if (!selected) return 'Aún no has seleccionado archivos.';
      return `${selected} archivo${selected === 1 ? '' : 's'} seleccionado${selected === 1 ? '' : 's'}.`;
    },
    contextReadinessMessage() {
      if (!this.selectedSourceIds.length) return 'Selecciona al menos un archivo para continuar.';
      if (this.uncoveredNecessaryRequestItems.length) {
        return (
          'Puedes continuar con esta selección. Quedarán pendientes: ' +
          `${this.uncoveredNecessaryRequestItems.map(item => item.label).join(', ')}.`
        );
      }
      if (this.structuredRequestItems.length && this.selectedSourceSelections.some(item => !item.request_item_id)) {
        return 'Indica qué solicitud cubre cada archivo seleccionado.';
      }
      return 'Todo listo. Puedes continuar la respuesta.';
    },
    contextContinueLabel() {
      const count = this.selectedSourceIds.length;
      return count
        ? `Continuar con ${count} archivo${count === 1 ? '' : 's'}`
        : 'Continuar con los archivos';
    },
    selectedContextSourcesReady() {
      if (!this.structuredRequestItems.length) return this.selectedSourceIds.length > 0;
      return !!(
        this.selectedSourceIds.length
        && this.selectedSourceSelections.every(selection => selection.request_item_id)
      );
    },
    plainArtifactText() {
      if (!this.renderedArtifact) return '';
      try {
        const container = document.createElement('div');
        container.innerHTML = this.renderedArtifact;
        return String(container.innerText || container.textContent || '').trim();
      } catch (error) {
        return String(this.projection && this.projection.content_markdown || '').trim();
      }
    },
    isContextRequest() {
      return !!(this.resultRef && this.resultRef.type === 'context_request');
    },
    isContinuation() {
      return !!(this.resultRef && this.resultRef.type === 'continuation');
    },
    continuationActions() {
      const actions = this.receipt && this.receipt.ui_payload && this.receipt.ui_payload.actions;
      if (!Array.isArray(actions)) return [];
      return actions.map((action) => String(action || '')
        .trim()
        .toLocaleLowerCase('es-ES')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, ''));
    },
    continuationCanRetry() {
      return this.isContinuation && this.continuationActions.includes('reintentar respuesta');
    },
    continuationCanStop() {
      return this.isContinuation && this.continuationActions.includes('detener');
    },
  },
  watch: {
    receipt: {
      immediate: true,
      handler(value, previousValue) {
        const receiptIdentity = (receipt) => [
          String(receipt && receipt.tool_run_id || ''),
          String(receipt && receipt.result_ref && receipt.result_ref.type || ''),
          String(receipt && receipt.result_ref && receipt.result_ref.id || ''),
          String(receipt && receipt.result_ref && receipt.result_ref.resolution_epoch || '0'),
          String(receipt && receipt.requested_artifact_version || ''),
        ].join('|');
        const nextId = receiptIdentity(value);
        const previousId = receiptIdentity(previousValue);
        if (!value) {
          this.resetReceiptState();
          return;
        }
        if (nextId !== previousId) {
          this.resetReceiptState();
          this.focusWorkspace();
          this.load();
          return;
        }
        const nextVersion = Number(value && value.state_version || 0);
        const previousVersion = Number(previousValue && previousValue.state_version || 0);
        if (
          nextVersion !== previousVersion
          && value.result_ref
          && value.result_ref.type === 'review'
        ) {
          // Finding decisions bump the durable receipt. Reconcile that new
          // state without replacing the open review with a loading screen.
          const viewportAnchor = this.activeReviewViewportAnchor
            || this.captureReviewViewportAnchor(
              this.activeFindingMutationId,
              typeof document !== 'undefined' ? document.activeElement : null,
            );
          this.load({
            silent: !!this.projection,
            preserveReviewOrder: true,
            viewportAnchor,
          });
        }
      },
    },
    activeReview(value, previousValue) {
      const nextSnapshot = createReviewApplySnapshot(value);
      const nextSignature = activeReviewMaterialSignature(value);
      if (
        !nextSnapshot
        || !nextSignature
        || nextSignature === activeReviewMaterialSignature(previousValue)
        || !this.projection
        || !this.resultRef
        || this.resultRef.type !== 'review'
        || nextSnapshot.reviewId !== String(this.resultRef.id || '')
        || this.mutationBusy
        || this.reviewDecisionConfirmationPending
      ) return;
      const viewportAnchor = this.captureReviewViewportAnchor(
        this.activeFindingMutationId,
        typeof document !== 'undefined' ? document.activeElement : null,
      );
      this.load({
        silent: true,
        preserveReviewOrder: true,
        viewportAnchor,
      });
    },
    reviewFocusMode(value) {
      const mode = value === 'pending' ? 'pending' : 'all';
      if (mode === this.reviewFilterMode) return;
      this.reviewFilterMode = mode;
      if (mode === 'all') this.reviewDecisionReceipts = {};
      if (this.projection) {
        this.preparePendingReviewView();
      } else {
        this.reviewPendingTitlesReady = false;
      }
    },
    inlinePassages: {
      deep: true,
      handler() {
        if (!this.receipt || !this.isInlineSourceRead) return;
        this.cancelLoad();
        this.loading = false;
        this.error = '';
        this.loadRetryable = false;
        this.loadFailure = null;
        this.loadRetryVersion = null;
        this.projection = this.inlineSourceProjection();
      },
    },
  },
  mounted() {
    document.addEventListener('keydown', this.onKeydown);
    this.mountResponseAudio();
  },
  updated() { this.mountResponseAudio(); },
  beforeUnmount() {
    document.removeEventListener('keydown', this.onKeydown);
    this.disposeReviewHelpPopover();
    if (this.copyPassageTimer) window.clearTimeout(this.copyPassageTimer);
    this.cancelLoad();
    this.cancelContextSearch();
    this.receiptGeneration += 1;
    this.cancelMutationRequest();
  },
  methods: {
    mountResponseAudio() {
      if (typeof window === 'undefined' || this.resultRef?.type !== 'document') return;
      window.LexiusResponseAudioAdapters?.mountDocumentVersion(this.$refs?.responseAudioActions, {
        chatUuid: this.chatUuid,
        artifactId: this.resultRef.id,
        version: this.projection?.selected_version,
        projection: this.projection,
        completed: Boolean(this.projection && !this.artifactProvenanceUnavailable),
        anchor: this.$el,
      });
    },
    renderMarkdown(value) {
      const source = String(value == null ? '' : value);
      if (!source) return '';
      try {
        const html = window.marked && typeof window.marked.parse === 'function'
          ? window.marked.parse(source, { headerIds: false, mangle: false })
          : escapePlaintextHtml(source);
        return sanitizeRichHtml(html, source, {
          FORBID_TAGS: ['script', 'style', 'iframe', 'object', 'embed', 'form', 'img', 'video', 'audio'],
          FORBID_ATTR: ['style', 'onerror', 'onclick', 'srcset'],
        });
      } catch (e) {
        return escapePlaintextHtml(source);
      }
    },
    sourcePassageMatch(sourceValue, passageValue) {
      return findSourcePassageMatch(sourceValue, passageValue);
    },
    exactSourceHasHighlight(sourceValue, passageValue) {
      return !!this.sourcePassageMatch(sourceValue, passageValue);
    },
    renderHighlightedSource(sourceValue, passageValue) {
      const source = String(sourceValue == null ? '' : sourceValue);
      if (!source) return '';
      const match = this.sourcePassageMatch(source, passageValue);
      if (!match) return this.renderMarkdown(source);
      const html = [
        escapePlaintextHtml(source.slice(0, match.start)),
        '<mark class="tool-review-source-highlight" data-source-passage-highlight="true">',
        escapePlaintextHtml(source.slice(match.start, match.end)),
        '</mark>',
        escapePlaintextHtml(source.slice(match.end)),
      ].join('');
      return sanitizeRichHtml(html, source, {
        ADD_TAGS: ['mark'],
        ADD_ATTR: ['class', 'data-source-passage-highlight'],
        FORBID_TAGS: ['script', 'style', 'iframe', 'object', 'embed', 'form', 'img', 'video', 'audio'],
        FORBID_ATTR: ['style', 'onerror', 'onclick', 'srcset'],
      });
    },
    resetReceiptState() {
      this.disposeReviewHelpPopover();
      this.receiptGeneration += 1;
      this.cancelLoad();
      this.cancelContextSearch();
      this.cancelMutationRequest();
      this.loading = false;
      this.contextLoading = false;
      this.error = '';
      this.loadRetryable = false;
      this.loadFailure = null;
      this.loadRetryVersion = null;
      this.projection = null;
      this.versions = [];
      this.anchorEvidenceById = {};
      this.openAnchorIds = {};
      this.loadingAnchorIds = {};
      this.anchorLoadErrors = {};
      this.pendingExactSourceKey = '';
      this.dispositionReasonDrafts = {};
      this.copiedPassageKey = '';
      this.copyPassageErrorKey = '';
      if (this.copyPassageTimer) window.clearTimeout(this.copyPassageTimer);
      this.copyPassageTimer = null;
      this.mutationBusy = false;
      this.reviewDecisionConfirmationPending = false;
      this.contextQuery = '';
      this.contextSources = [];
      this.selectedSourceIds = [];
      this.sourceRequestItemIds = {};
      this.contextCursor = null;
      this.contextResolutionEpoch = Math.max(
        0,
        Number.parseInt(String(this.resultRef && this.resultRef.resolution_epoch || 0), 10) || 0,
      );
      // Keep unresolved-operation keys across panel close/reopen. A lost
      // response must retry the same logical mutation with the same key.
      this.uploadingContextDocument = false;
      this.contextUploadMessage = '';
      this.activeFindingMutationId = '';
      this.activeReviewViewportAnchor = null;
      this.reviewFilterMode = this.reviewFocusMode === 'pending' ? 'pending' : 'all';
      this.reviewPendingTitlesReady = false;
      this.reviewDecisionReceipts = {};
      this.reviewApplyConfirmation = null;
      this.handledReviewApplySnapshotKey = '';
      this.savedFindingId = '';
      this.artifactVersionOverride = null;
    },
    inlineSourceProjection() {
      const passages = Array.isArray(this.inlinePassages)
        ? this.inlinePassages.filter(item => (
          item && typeof item === 'object' && typeof item.quote === 'string'
        ))
        : [];
      return {
        passages,
        sources_authorized: new Set(passages
          .map(item => String(item.source_title || '').trim())
          .filter(Boolean)).size,
      };
    },
    mutationTimeoutMs() {
      const override = Number(
        typeof window !== 'undefined' && window.__LEXIUS_TOOL_MUTATION_TIMEOUT_MS,
      );
      return Number.isFinite(override) && override > 0
        ? override
        : DEFAULT_MUTATION_TIMEOUT_MS;
    },
    cancelMutationRequest() {
      if (this.mutationTimeoutId) {
        clearTimeout(this.mutationTimeoutId);
        this.mutationTimeoutId = null;
      }
      if (this.mutationController) {
        this.mutationController.abort();
        this.mutationController = null;
      }
    },
    async fetchMutationAttempt(url, options, timeoutMs) {
      const controller = new AbortController();
      let timedOut = false;
      const timeoutId = setTimeout(() => {
        timedOut = true;
        controller.abort();
      }, timeoutMs);
      this.mutationController = controller;
      this.mutationTimeoutId = timeoutId;
      try {
        return await fetch(url, { ...options, signal: controller.signal });
      } catch (error) {
        if (timedOut) {
          const timeoutError = new Error('No pudimos confirmar todavía si la operación se guardó.');
          timeoutError.name = 'MutationTimeoutError';
          throw timeoutError;
        }
        throw error;
      } finally {
        clearTimeout(timeoutId);
        if (this.mutationController === controller) this.mutationController = null;
        if (this.mutationTimeoutId === timeoutId) this.mutationTimeoutId = null;
      }
    },
    async queryAuthoritativeToolRun(identity) {
      if (!identity || !identity.toolRunId || !identity.chatUuid) return null;
      const query = new URLSearchParams({ chat_uuid: identity.chatUuid });
      const response = await this.fetchMutationAttempt(
        `/chat/tool-runs/${encodeURIComponent(identity.toolRunId)}?${query.toString()}`,
        { method: 'GET' },
        MUTATION_RECONCILIATION_TIMEOUT_MS,
      );
      if (!response.ok) return null;
      return response.json().catch(() => null);
    },
    async fetchMutationWithReconciliation(url, options, identity) {
      try {
        return await this.fetchMutationAttempt(url, options, this.mutationTimeoutMs());
      } catch (error) {
        if (error && error.name !== 'MutationTimeoutError') throw error;
        if (!this.isCurrentMutationIdentity(identity)) throw error;

        // The request may already have committed after the browser lost its
        // response. Do not claim that it was saved until a newer durable
        // receipt proves a state transition.
        this.error = 'Verificando si la operación se guardó…';
        let durableCommitConfirmed = false;
        try {
          const snapshot = await this.queryAuthoritativeToolRun(identity);
          const receipt = snapshot && snapshot.receipt && typeof snapshot.receipt === 'object'
            ? snapshot.receipt
            : snapshot;
          durableCommitConfirmed = !!(
            receipt
            && Number(receipt.state_version || 0) > Number(identity.receiptStateVersion || 0)
          );
        } catch (reconciliationError) {
          if (!this.isCurrentMutationIdentity(identity)) throw reconciliationError;
        }
        if (!this.isCurrentMutationIdentity(identity)) throw error;
        this.error = durableCommitConfirmed
          ? 'Guardado; reconectando con la conversación…'
          : 'Resultado aún incierto; reconectando con la misma operación…';
        try {
          return await this.fetchMutationAttempt(
            url,
            options,
            MUTATION_RECONCILIATION_TIMEOUT_MS,
          );
        } catch (replayError) {
          const outcomeUnknown = new Error(
            'No pudimos confirmar el resultado. Reintenta: conservaremos la misma clave para evitar duplicados.',
          );
          outcomeUnknown.name = 'MutationOutcomeUnknownError';
          throw outcomeUnknown;
        }
      }
    },
    focusWorkspace() {
      this.$nextTick(() => {
        const content = this.$refs.content;
        if (content) content.scrollTop = 0;
        const panel = this.$refs.panel;
        if (panel && typeof panel.focus === 'function') {
          panel.focus({ preventScroll: true });
        }
        this.initializeReviewHelpPopover();
      });
    },
    initializeReviewHelpPopover() {
      const trigger = this.$refs.reviewHelpButton;
      if (!trigger || !(this.resultRef && this.resultRef.type === 'review')) return;
      const content = String(trigger.getAttribute('data-content') || '').trim();
      try {
        if (window.jQuery && typeof window.jQuery(trigger).popover === 'function') {
          const popoverTrigger = window.jQuery(trigger);
          if (!popoverTrigger.data('bs.popover')) {
            popoverTrigger.popover({
              trigger: 'focus',
              html: true,
              placement: 'bottom',
              container: 'body',
              boundary: 'viewport',
              customClass: 'lexius-help-popover--document-review',
              content,
            });
          }
          return;
        }
        const Popover = window.bootstrap && window.bootstrap.Popover;
        if (!Popover) return;
        if (typeof Popover.getOrCreateInstance === 'function') {
          Popover.getOrCreateInstance(trigger, {
            trigger: 'focus',
            html: true,
            placement: 'bottom',
            container: 'body',
            boundary: 'viewport',
            customClass: 'lexius-help-popover--document-review',
            content,
          });
        } else if (!Popover.getInstance || !Popover.getInstance(trigger)) {
          new Popover(trigger, {
            trigger: 'focus',
            html: true,
            placement: 'bottom',
            container: 'body',
            boundary: 'viewport',
            customClass: 'lexius-help-popover--document-review',
            content,
          });
        }
      } catch (error) { }
    },
    disposeReviewHelpPopover() {
      const trigger = this.$refs.reviewHelpButton;
      if (!trigger) return;
      try {
        const instance = window.bootstrap?.Popover?.getInstance?.(trigger);
        if (instance?.dispose) instance.dispose();
      } catch (error) { }
      try {
        if (window.jQuery && typeof window.jQuery(trigger).popover === 'function') {
          const popoverTrigger = window.jQuery(trigger);
          if (popoverTrigger.data('bs.popover')) popoverTrigger.popover('dispose');
        }
      } catch (error) { }
    },
    setReviewFilter(mode) {
      const nextMode = mode === 'pending' ? 'pending' : 'all';
      if (nextMode === this.reviewFilterMode) return;
      this.reviewFilterMode = nextMode;
      if (nextMode === 'all') this.reviewDecisionReceipts = {};
      this.preparePendingReviewView();
    },
    pendingReviewDecisionReceipt(finding) {
      if (!this.reviewIsPendingFilter || !finding) return null;
      const findingId = String(finding.finding_id || '');
      const receipt = findingId && this.reviewDecisionReceipts
        ? this.reviewDecisionReceipts[findingId]
        : null;
      const disposition = String(finding.disposition || 'open').toLowerCase();
      const stateVersion = this.findingStateVersion(finding);
      return receipt && disposition !== 'open'
        && String(receipt.disposition || '').toLowerCase() === disposition
        && Number(receipt.stateVersion || 1) === stateVersion
        ? receipt
        : null;
    },
    reviewDecisionReceiptLabel(finding) {
      const disposition = String(finding && finding.disposition || '').toLowerCase();
      if (disposition === 'accepted') {
        return this.reviewUsesConsiderActions
          ? 'Hallazgo considerado'
          : 'Recomendación aprobada';
      }
      if (disposition === 'intentional') return 'Se mantendrá como está';
      if (disposition === 'dismissed') return 'Hallazgo descartado';
      return 'Decisión guardada';
    },
    recordPendingReviewDecisionReceipt(finding) {
      if (!this.reviewIsPendingFilter || !finding || !finding.finding_id) return;
      const disposition = String(finding.disposition || 'open').toLowerCase();
      if (disposition === 'open') return;
      this.setReactiveMapValue('reviewDecisionReceipts', String(finding.finding_id), {
        disposition,
        stateVersion: this.findingStateVersion(finding),
      });
    },
    dismissPendingReviewDecisionReceipt(findingId) {
      const normalizedFindingId = String(findingId || '');
      if (!normalizedFindingId || !this.reviewDecisionReceipts[normalizedFindingId]) return;
      const receiptElement = this.reviewFindingElement(normalizedFindingId);
      const nextFindingId = String(
        receiptElement?.nextElementSibling?.dataset?.findingId || '',
      );
      const receipts = { ...this.reviewDecisionReceipts };
      delete receipts[normalizedFindingId];
      this.reviewDecisionReceipts = receipts;
      if (typeof this.focusReviewTarget === 'function') {
        this.focusReviewTarget(nextFindingId);
      }
    },
    focusReviewTarget(findingId = '') {
      const nextTick = typeof this.$nextTick === 'function'
        ? callback => this.$nextTick(callback)
        : callback => callback();
      nextTick(() => {
        const target = (findingId && this.reviewFindingElement(findingId))
          || (this.$refs && this.$refs.reviewPendingFilterButton)
          || (this.$refs && this.$refs.panel);
        if (!target || typeof target.focus !== 'function') return;
        try {
          target.focus({ preventScroll: true });
        } catch (error) {
          // A concurrent refresh may remove the selected target.
        }
      });
    },
    preparePendingReviewView() {
      this.reviewPendingTitlesReady = false;
      const nextTick = typeof this.$nextTick === 'function'
        ? callback => this.$nextTick(callback)
        : callback => callback();
      nextTick(() => {
        if (!this.visibleOpenReviewFindings.length) {
          return;
        }
        // The actions are deliberately added on the render after the compact
        // title list, so a bulk decision is never offered without context.
        this.reviewPendingTitlesReady = true;
      });
    },
    reviewApplyFindingSummary(finding, index) {
      const evidence = finding && finding.evidence && typeof finding.evidence === 'object'
        ? finding.evidence
        : {};
      return {
        findingId: String(finding && finding.finding_id || ''),
        ordinal: finding ? this.reviewFindingOrdinal(finding) : index + 1,
        title: String(finding && finding.title || `Recomendación ${index + 1}`).trim(),
        recommendation: String(evidence.recommendation || '').trim(),
      };
    },
    reviewApplySnapshotKey(snapshotSource) {
      const snapshot = createReviewApplySnapshot(snapshotSource);
      if (!snapshot) return '';
      return JSON.stringify([
        snapshot.reviewId,
        snapshot.materialState,
        snapshot.approvedFindingIds,
      ]);
    },
    openRequestedReviewApplyConfirmation() {
      const snapshotSource = this.receipt && this.receipt.review_apply_snapshot;
      const requestKey = this.reviewApplySnapshotKey(snapshotSource);
      if (!requestKey || requestKey === this.handledReviewApplySnapshotKey) return false;
      const opened = this.openReviewApplyConfirmation(snapshotSource);
      if (opened) this.handledReviewApplySnapshotKey = requestKey;
      return opened;
    },
    openReviewApplyConfirmation(snapshotSource = null) {
      const snapshot = createReviewApplySnapshot(
        snapshotSource || this.activeReviewApplySnapshot,
      );
      const canApplySnapshot = Boolean(
        this.reviewIsEditable
        && snapshot
        && snapshot.reviewId === String(this.resultRef && this.resultRef.id || '')
        && snapshot.materialState
        && snapshot.approvedCount > 0
        && snapshot.approvedFindingIds.length === snapshot.approvedCount
        && snapshot.approvedFindingIds.every(identifier => !!identifier)
        && new Set(snapshot.approvedFindingIds).size === snapshot.approvedFindingIds.length
      );
      if (!canApplySnapshot) return false;
      const findingById = new Map(this.findings.map(finding => [
        String(finding && finding.finding_id || ''),
        finding,
      ]));
      this.reviewApplyConfirmation = {
        snapshot,
        documentTitle: String(
          this.projection && (
            this.projection.document_title
            || this.projection.source_title
            || this.projection.source_handle
          ) || '',
        ).trim(),
        approved: snapshot.approvedFindingIds.map((findingId, index) => (
          this.reviewApplyFindingSummary(findingById.get(findingId), index)
        )),
        pending: this.reviewPendingFindings.map((finding, index) => (
          this.reviewApplyFindingSummary(finding, index)
        )),
      };
      const nextTick = typeof this.$nextTick === 'function'
        ? callback => this.$nextTick(callback)
        : callback => callback();
      nextTick(() => {
        const button = this.$refs && this.$refs.reviewApplyConfirmButton;
        if (button && typeof button.focus === 'function') button.focus({ preventScroll: true });
      });
      return true;
    },
    dismissReviewApplyConfirmation({ restoreFocus = true } = {}) {
      if (!this.reviewApplyConfirmation) return;
      this.reviewApplyConfirmation = null;
      if (!restoreFocus) return;
      const nextTick = typeof this.$nextTick === 'function'
        ? callback => this.$nextTick(callback)
        : callback => callback();
      nextTick(() => {
        const trigger = this.$refs && this.$refs.reviewApplyTrigger;
        if (trigger && typeof trigger.focus === 'function') trigger.focus({ preventScroll: true });
      });
    },
    returnToPendingReviewFindings() {
      this.dismissReviewApplyConfirmation({ restoreFocus: false });
      this.reviewFilterMode = 'pending';
      this.preparePendingReviewView();
    },
    confirmApplyApprovedReviewChanges() {
      const confirmation = this.reviewApplyConfirmation;
      if (!confirmation) return;
      this.reviewApplyConfirmation = null;
      this.requestApplyApprovedReviewChanges(confirmation.snapshot);
    },
    requestApplyApprovedReviewChanges(snapshotSource = null) {
      if (!this.reviewCanApplyApproved) return;
      if (typeof this.$emit !== 'function') return;
      const snapshot = createReviewApplySnapshot(
        snapshotSource || this.activeReviewApplySnapshot,
      );
      if (!snapshot) return;
      this.$emit('review-action', {
        action: 'apply_review_changes',
        review_id: snapshot.reviewId,
        material_state: snapshot.materialState,
        approved_finding_ids: [...snapshot.approvedFindingIds],
        approved_count: snapshot.approvedCount,
        applySnapshot: snapshot,
      });
    },
    reviewFindingOrdinal(finding) {
      const findingId = String(finding && finding.finding_id || '');
      const index = this.findings.findIndex(item => (
        String(item && item.finding_id || '') === findingId
      ));
      return index >= 0 ? index + 1 : 1;
    },
    scrollToReviewFinding(finding) {
      const findingId = String(finding && finding.finding_id || '').trim();
      if (!findingId || typeof this.$nextTick !== 'function') return;
      this.$nextTick(() => {
        const content = this.$refs && this.$refs.content;
        if (!content || typeof content.querySelectorAll !== 'function') return;
        const target = Array.from(content.querySelectorAll('[data-finding-id]'))
          .find(element => (
            String(element.dataset && element.dataset.findingId || '') === findingId
          ));
        if (!target || typeof target.scrollIntoView !== 'function') return;
        const reduceMotion = typeof window !== 'undefined'
          && typeof window.matchMedia === 'function'
          && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        target.scrollIntoView({
          behavior: reduceMotion ? 'auto' : 'smooth',
          block: 'start',
          inline: 'nearest',
        });
      });
    },
    cancelLoad() {
      this.loadGeneration += 1;
      if (this.loadController) {
        this.loadController.abort();
        this.loadController = null;
      }
    },
    startLoad() {
      if (this.loadController) this.loadController.abort();
      const token = ++this.loadGeneration;
      const controller = new AbortController();
      this.loadController = controller;
      return { token, controller };
    },
    isCurrentLoad(token) {
      return this.isOpen && token === this.loadGeneration;
    },
    cancelContextSearch() {
      this.contextSearchGeneration += 1;
      if (this.contextSearchController) {
        this.contextSearchController.abort();
        this.contextSearchController = null;
      }
    },
    startContextSearch() {
      if (this.contextSearchController) this.contextSearchController.abort();
      const token = ++this.contextSearchGeneration;
      const controller = new AbortController();
      this.contextSearchController = controller;
      return { token, controller };
    },
    isCurrentContextSearch(token) {
      return this.isOpen && token === this.contextSearchGeneration;
    },
    isAbortError(error) {
      return !!(error && (error.name === 'AbortError' || error.code === 20));
    },
    endpoint() {
      if (!this.resultRef || !this.resultRef.id || !this.chatUuid) return '';
      const id = encodeURIComponent(String(this.resultRef.id));
      const query = new URLSearchParams({ chat_uuid: this.chatUuid });
      if (this.resultRef.type === 'document' && this.requestedArtifactVersion) {
        query.set('version', String(this.requestedArtifactVersion));
      }
      if (this.resultRef.type === 'document') return `/chat/document-artifacts/${id}?${query}`;
      if (this.resultRef.type === 'review') return `/chat/document-reviews/${id}?${query}`;
      if (this.resultRef.type === 'anchor_set') return `/chat/source-anchors/${id}?${query}`;
      return '';
    },
    projectionWithStableReviewOrder(projection, previousFindings = []) {
      if (
        !projection
        || !Array.isArray(projection.findings)
        || !Array.isArray(previousFindings)
        || !previousFindings.length
      ) return projection;
      const incomingById = new Map(projection.findings.map(finding => [
        String(finding && finding.finding_id || ''),
        finding,
      ]));
      // Keep the order the reader already learned for this open workspace
      // while still accepting every new row from a concurrent refresh.
      const ordered = previousFindings
        .map(finding => incomingById.get(String(finding && finding.finding_id || '')))
        .filter(Boolean);
      const retainedIds = new Set(ordered.map(finding => String(finding.finding_id || '')));
      projection.findings.forEach((finding) => {
        if (!retainedIds.has(String(finding && finding.finding_id || ''))) ordered.push(finding);
      });
      return { ...projection, findings: ordered };
    },
    reviewFindingElement(findingId) {
      const content = this.$refs.content;
      if (!content || !findingId) return null;
      return Array.from(content.querySelectorAll('.tool-finding')).find(element => (
        String(element.dataset && element.dataset.findingId || '') === String(findingId)
      )) || null;
    },
    captureReviewViewportAnchor(findingId = '', preferredElement = null) {
      const content = this.$refs.content;
      if (!content || !this.projection || !this.resultRef || this.resultRef.type !== 'review') return null;
      const usablePreferred = preferredElement
        && typeof preferredElement.getBoundingClientRect === 'function'
        && content.contains(preferredElement)
        ? preferredElement
        : null;
      const preferredFinding = usablePreferred && typeof usablePreferred.closest === 'function'
        ? usablePreferred.closest('.tool-finding')
        : null;
      const resolvedFindingId = String(
        findingId
        || (preferredFinding && preferredFinding.dataset && preferredFinding.dataset.findingId)
        || '',
      );
      const findingElement = preferredFinding || this.reviewFindingElement(resolvedFindingId);
      if (!findingElement || !resolvedFindingId) return null;
      const target = usablePreferred && findingElement.contains(usablePreferred)
        ? usablePreferred
        : findingElement;
      const targetRect = target.getBoundingClientRect();
      const findingRect = findingElement.getBoundingClientRect();
      return {
        findingId: resolvedFindingId,
        action: String(target.dataset && target.dataset.reviewAction || ''),
        viewportTop: targetRect.top,
        offsetWithinFinding: targetRect.top - findingRect.top,
      };
    },
    restoreReviewViewportAnchor(anchor) {
      if (!anchor || !anchor.findingId) return;
      this.$nextTick(() => {
        const restore = () => {
          const content = this.$refs.content;
          const findingElement = this.reviewFindingElement(anchor.findingId);
          if (!content || !findingElement) return;
          const actionElement = anchor.action
            ? Array.from(findingElement.querySelectorAll('[data-review-action]')).find(element => (
              String(element.dataset && element.dataset.reviewAction || '') === anchor.action
            ))
            : null;
          const currentTop = actionElement
            ? actionElement.getBoundingClientRect().top
            : findingElement.getBoundingClientRect().top + Number(anchor.offsetWithinFinding || 0);
          const delta = currentTop - Number(anchor.viewportTop || 0);
          if (Number.isFinite(delta) && Math.abs(delta) >= 0.5) content.scrollTop += delta;
        };
        restore();
        if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
          window.requestAnimationFrame(restore);
        }
      });
    },
    async load(options = {}) {
      const silent = options.silent === true && !!this.projection;
      const previousFindings = options.preserveReviewOrder && this.projection && Array.isArray(this.projection.findings)
        ? this.projection.findings.slice()
        : [];
      if (!silent) {
        this.error = '';
        this.loadRetryable = false;
        this.loadFailure = null;
        this.loadRetryVersion = null;
      }
      if (this.isContinuation) {
        return;
      }
      if (this.isContextRequest) {
        await this.searchContextSources(true);
        return;
      }
      if (this.isInlineSourceRead) {
        this.projection = this.inlineSourceProjection();
        return;
      }
      const endpoint = this.endpoint();
      if (!endpoint) return;
      const { token, controller } = this.startLoad();
      let requestEndpoint = endpoint;
      if (this.resultRef && this.resultRef.type === 'review') {
        const nonce = (
          globalThis.crypto
          && typeof globalThis.crypto.randomUUID === 'function'
        )
          ? globalThis.crypto.randomUUID()
          : `${Date.now()}-${token}-${Math.random().toString(36).slice(2)}`;
        requestEndpoint += `${endpoint.includes('?') ? '&' : '?'}_review_state_nonce=${encodeURIComponent(nonce)}`;
      }
      if (!silent) this.loading = true;
      try {
        const response = await fetch(requestEndpoint, {
          signal: controller.signal,
          cache: 'no-store',
        });
        const data = await response.json().catch(() => ({}));
        const deniedArtifact = this.resultRef && this.resultRef.type === 'document'
          && response.status === 403 && data.available === false;
        if (!response.ok && !deniedArtifact) {
          throw createToolResultAccessError(toolResultAccessFeedback({
            response,
            resultType: this.resultRef && this.resultRef.type,
            retryable: data.retryable === true,
          }));
        }
        if (!this.isCurrentLoad(token)) return;
        this.loadFailure = null;
        this.projection = options.preserveReviewOrder
          ? this.projectionWithStableReviewOrder(data, previousFindings)
          : data;
        if (typeof this.openRequestedReviewApplyConfirmation === 'function') {
          this.openRequestedReviewApplyConfirmation();
        }
        this.restoreReviewViewportAnchor(options.viewportAnchor);
        if (
          this.resultRef
          && this.resultRef.type === 'review'
          && typeof this.preparePendingReviewView === 'function'
        ) {
          this.preparePendingReviewView();
          if (typeof this.$emit === 'function') {
            // The authenticated GET persists "opened" and can reactivate an
            // older review. Rehydrate the shared composer/card state even
            // when the realtime wake-up is delayed or unavailable.
            this.$emit('review-state-changed', null);
          }
        }
        if (this.resultRef && this.resultRef.type === 'document') {
          const query = `chat_uuid=${encodeURIComponent(this.chatUuid)}`;
          try {
            const versionsResponse = await fetch(
              `/chat/document-artifacts/${encodeURIComponent(String(this.resultRef.id))}/versions?${query}`,
              { signal: controller.signal },
            );
            const versionsData = await versionsResponse.json().catch(() => ({}));
            if (!this.isCurrentLoad(token)) return;
            this.versions = versionsResponse.ok && Array.isArray(versionsData.versions)
              ? versionsData.versions
              : [];
          } catch (error) {
            // Version history is supplementary. Keep the successfully loaded
            // document visible when only this secondary request is unavailable.
            if (this.isCurrentLoad(token)) this.versions = [];
          }
        }
      } catch (error) {
        if (this.isAbortError(error) || !this.isCurrentLoad(token)) return;
        if (!silent) {
          const feedback = toolResultAccessFeedback({
            error,
            resultType: this.resultRef && this.resultRef.type,
          });
          this.loadFailure = feedback;
          this.error = feedback.message;
          this.loadRetryable = feedback.retryable;
        }
      } finally {
        if (this.isCurrentLoad(token)) {
          if (!silent) this.loading = false;
          this.loadController = null;
          if (this.resultRef && this.resultRef.type === 'review' && this.projection) {
            this.$nextTick(() => {
              window.dispatchEvent(new CustomEvent('lexius-document-review-opened'));
            });
          }
        }
      }
    },
    async searchContextSources(reset = true) {
      if (!this.isContextRequest || this.contextLoading) return false;
      const { token, controller } = this.startContextSearch();
      const receiptId = String(this.receipt && this.receipt.tool_run_id || '');
      const requestId = String(this.resultRef && this.resultRef.id || '');
      const expectedResolutionEpoch = this.contextResolutionEpoch;
      this.contextLoading = true;
      this.loading = true;
      this.error = '';
      try {
        const response = await fetch(`/chat/context-requests/${encodeURIComponent(String(this.resultRef.id))}/sources/search`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          signal: controller.signal,
          body: JSON.stringify({
            chat_uuid: this.chatUuid,
            query: String(this.contextQuery || '').slice(0, 120),
            cursor: reset ? null : this.contextCursor,
            expected_resolution_epoch: expectedResolutionEpoch,
          }),
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(data.message || 'No se pudieron cargar los documentos.');
        if (
          !this.isCurrentContextSearch(token)
          || String(this.receipt && this.receipt.tool_run_id || '') !== receiptId
          || String(this.resultRef && this.resultRef.id || '') !== requestId
        ) return false;
        const returnedEpoch = Number.parseInt(String(data.resolution_epoch), 10);
        if (Number.isInteger(returnedEpoch) && returnedEpoch >= 0) {
          this.contextResolutionEpoch = returnedEpoch;
        }
        const incoming = Array.isArray(data.items) ? data.items : [];
        this.contextSources = reset ? incoming : this.contextSources.concat(incoming);
        this.contextCursor = typeof data.cursor === 'string' ? data.cursor : null;
        return true;
      } catch (error) {
        if (this.isAbortError(error) || !this.isCurrentContextSearch(token)) return false;
        this.error = String(error && error.message ? error.message : 'No se pudieron cargar los documentos.');
        return false;
      } finally {
        if (this.isCurrentContextSearch(token)) {
          this.contextLoading = false;
          this.loading = false;
          this.contextSearchController = null;
        }
      }
    },
    toggleContextSource(sourceId) {
      const id = String(sourceId || '');
      if (!id) return;
      const current = this.selectedSourceIds.slice();
      const index = current.indexOf(id);
      if (index >= 0) {
        current.splice(index, 1);
        delete this.sourceRequestItemIds[id];
      } else if (current.length < this.maxContextSelectionSources) {
        const source = this.contextSourceById(id);
        const compatible = this.compatibleRequestItems(source);
        if (this.structuredRequestItems.length && !compatible.length) {
          this.error = 'Este archivo no tiene un tipo aceptado para la solicitud.';
          return;
        }
        current.push(id);
        this.assignContextSourceRequestItem(id, source);
      }
      this.selectedSourceIds = current;
      this.error = '';
    },
    contextSourceById(sourceId) {
      const id = String(sourceId || '');
      return this.contextSources.find(source => String(source && source.source_id || '') === id) || null;
    },
    isContextSourceSelected(source) {
      return this.selectedSourceIds.includes(String(source && source.source_id || ''));
    },
    isContextSourceCompatible(source) {
      return !this.structuredRequestItems.length || this.compatibleRequestItems(source).length > 0;
    },
    contextSourceDisabledReason(source) {
      if (!this.isContextSourceCompatible(source)) return 'El tipo de este archivo no coincide con la solicitud.';
      if (
        !this.isContextSourceSelected(source)
        && this.selectedSourceIds.length >= this.maxContextSelectionSources
      ) return `Ya seleccionaste el máximo de ${this.maxContextSelectionSources} archivos.`;
      return '';
    },
    contextSourceIconClass(source) {
      return {
        imagen: 'anticon anticon-file-image',
        audio: 'anticon anticon-audio',
        video: 'anticon anticon-video-camera',
        documento: 'anticon anticon-file-text',
      }[this.contextSourceCategory(source)] || 'anticon anticon-file-text';
    },
    contextSourceCategory(source) {
      const sourceKind = String(source && source.source_kind || '').trim().toLocaleLowerCase('es-ES');
      if (sourceKind === 'image' || sourceKind === 'imagen') return 'imagen';
      if (sourceKind === 'audio') return 'audio';
      if (sourceKind === 'video') return 'video';
      return 'documento';
    },
    contextTypeLabel(type) {
      return ({ documento: 'documento', imagen: 'imagen', audio: 'audio', video: 'video' })[type]
        || String(type || 'documento');
    },
    compatibleRequestItems(source) {
      const category = this.contextSourceCategory(source);
      return this.structuredRequestItems.filter(item => item.acceptedTypes.includes(category));
    },
    assignContextSourceRequestItem(sourceId, source = null) {
      const id = String(sourceId || '');
      if (!id || !this.structuredRequestItems.length) return;
      const compatible = this.compatibleRequestItems(source || this.contextSourceById(id));
      const alreadyCovered = new Set(this.selectedSourceIds.map(selectedId => (
        selectedId === id ? '' : String(this.sourceRequestItemIds[String(selectedId)] || '')
      )).filter(Boolean));
      const chosen = compatible.find(item => (
        item.priority === 'necesario' && !alreadyCovered.has(item.requestItemId)
      )) || compatible.find(item => !alreadyCovered.has(item.requestItemId)) || compatible[0];
      if (chosen) this.sourceRequestItemIds[id] = chosen.requestItemId;
    },
    setContextSourceRequestItem(sourceId, requestItemId) {
      const id = String(sourceId || '');
      const requestedId = String(requestItemId || '');
      const compatible = this.compatibleRequestItems(this.contextSourceById(id));
      if (!compatible.some(item => item.requestItemId === requestedId)) return;
      this.sourceRequestItemIds[id] = requestedId;
      this.error = '';
    },
    captureMutationIdentity() {
      return Object.freeze({
        ...toolMutationIdentity(this.chatUuid, this.receipt),
        panelGeneration: this.receiptGeneration,
        receiptStateVersion: Number(this.receipt && this.receipt.state_version || 0),
        resolutionEpoch: this.contextResolutionEpoch,
      });
    },
    isCurrentMutationIdentity(identity) {
      return !!(
        identity
        && identity.panelGeneration === this.receiptGeneration
        && identity.resolutionEpoch === this.contextResolutionEpoch
        && sameToolMutationIdentity(identity, toolMutationIdentity(this.chatUuid, this.receipt))
      );
    },
    operationKey(scope, detail = '', identity = this.captureMutationIdentity()) {
      const key = toolOperationStorageKey(identity, scope, detail);
      if (this.operationKeys[key]) return this.operationKeys[key];
      const suffix = window.crypto && typeof window.crypto.randomUUID === 'function'
        ? window.crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      const value = `${scope}:${suffix}`.slice(0, 128);
      this.operationKeys[key] = value;
      return value;
    },
    releaseOperationKey(scope, detail = '', identity = this.captureMutationIdentity()) {
      delete this.operationKeys[toolOperationStorageKey(identity, scope, detail)];
    },
    async resolveContextRequest(resolution) {
      if (!this.isContextRequest || this.mutationBusy) return;
      if (resolution === 'selected' && !this.selectedSourceIds.length) {
        this.error = 'Selecciona al menos un documento.';
        return;
      }
      if (resolution === 'selected' && !this.selectedContextSourcesReady) {
        this.error = 'Indica qué elemento solicitado satisface cada documento.';
        return;
      }
      const selectedIds = resolution === 'selected' ? this.selectedSourceIds.slice() : [];
      const sourceSelections = resolution === 'selected' && this.structuredRequestItems.length
        ? this.selectedSourceSelections
        : [];
      const legacySourceIds = sourceSelections.length ? [] : selectedIds;
      const identity = this.captureMutationIdentity();
      const detail = JSON.stringify({
        context_request_id: identity.resourceId,
        expected_resolution_epoch: identity.resolutionEpoch,
        resolution,
        source_ids: legacySourceIds,
        source_selections: sourceSelections,
        attached_documents: [],
        resume_message: '',
      });
      const operationScope = `context-resolve-${resolution}`;
      const idempotencyKey = this.operationKey(operationScope, detail, identity);
      this.mutationBusy = true;
      this.error = '';
      try {
        const response = await this.fetchMutationWithReconciliation(`/chat/context-requests/${encodeURIComponent(identity.resourceId)}/resolve`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            chat_uuid: identity.chatUuid,
            resolution,
            source_ids: legacySourceIds,
            source_selections: sourceSelections,
            expected_resolution_epoch: identity.resolutionEpoch,
            idempotency_key: idempotencyKey,
          }),
        }, identity);
        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
          const mismatch = String(data.error || data.code || '') === 'idempotency_payload_mismatch';
          throw new Error(mismatch
            ? 'La selección cambió desde el intento anterior. Revísala y vuelve a enviarla; no se eliminó nada.'
            : (data.message || 'No se pudo resolver la solicitud.'));
        }
        this.releaseOperationKey(operationScope, detail, identity);
        if (!this.isCurrentMutationIdentity(identity)) return;
        this.$emit('resolved', data);
        this.close({ allowDuringMutation: true });
      } catch (error) {
        if (this.isCurrentMutationIdentity(identity)) {
          this.error = String(error && error.message ? error.message : 'No se pudo resolver la solicitud.');
        }
      } finally {
        if (this.isCurrentMutationIdentity(identity)) this.mutationBusy = false;
      }
    },
    async transitionContinuation(action) {
      if (!this.isContinuation || this.mutationBusy || !['stop', 'retry'].includes(action)) return;
      const operationScope = `continuation-${action}`;
      const identity = this.captureMutationIdentity();
      const idempotencyKey = this.operationKey(operationScope, '', identity);
      this.mutationBusy = true;
      this.error = '';
      try {
        const response = await this.fetchMutationWithReconciliation(`/chat/continuations/${encodeURIComponent(identity.resourceId)}/${action}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            chat_uuid: identity.chatUuid,
            idempotency_key: idempotencyKey,
          }),
        }, identity);
        const data = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(data.message || 'No se pudo actualizar la respuesta.');
        this.releaseOperationKey(operationScope, '', identity);
        if (!this.isCurrentMutationIdentity(identity)) return;
        this.$emit('resolved', data);
        this.close({ allowDuringMutation: true });
      } catch (error) {
        if (this.isCurrentMutationIdentity(identity)) {
          this.error = String(error && error.message ? error.message : 'No se pudo actualizar la respuesta.');
        }
      } finally {
        if (this.isCurrentMutationIdentity(identity)) this.mutationBusy = false;
      }
    },
    async openArtifactVersion(version) {
      if (!this.resultRef || this.loading) return;
      const requested = Number(version);
      const slot = this.versions.find(item => Number(item && item.version_number) === requested);
      if (!Number.isInteger(requested) || requested <= 0 || (slot && slot.available === false)) return;
      const { token, controller } = this.startLoad();
      this.loading = true;
      this.error = '';
      this.loadRetryable = false;
      this.loadFailure = null;
      this.loadRetryVersion = requested;
      try {
        const query = new URLSearchParams({ chat_uuid: this.chatUuid, version: String(version) });
        const response = await fetch(
          `/chat/document-artifacts/${encodeURIComponent(String(this.resultRef.id))}?${query.toString()}`,
          { signal: controller.signal },
        );
        const data = await response.json().catch(() => ({}));
        if (!response.ok && !(response.status === 403 && data.available === false)) {
          throw createToolResultAccessError(toolResultAccessFeedback({
            response,
            resultType: 'document',
            retryable: data.retryable === true,
          }));
        }
        if (!this.isCurrentLoad(token)) return;
        this.loadRetryVersion = null;
        this.projection = data;
      } catch (error) {
        if (this.isAbortError(error) || !this.isCurrentLoad(token)) return;
        const feedback = toolResultAccessFeedback({ error, resultType: 'document' });
        this.loadFailure = feedback;
        this.loadRetryable = feedback.retryable;
        this.error = feedback.message;
      } finally {
        if (this.isCurrentLoad(token)) {
          this.loading = false;
          this.loadController = null;
        }
      }
    },
    retryToolResultLoad() {
      const retryVersion = Number(this.loadRetryVersion);
      if (Number.isInteger(retryVersion) && retryVersion > 0) {
        return this.openArtifactVersion(retryVersion);
      }
      if (this.artifactProvenanceUnavailable) {
        this.artifactVersionOverride = this.projection.selected_version;
      }
      return this.load();
    },
    async restoreSelectedVersion() {
      if (!this.projection || this.mutationBusy) return;
      const selected = Number(this.projection.selected_version || 0);
      const current = Number(this.projection.current_version || 0);
      if (!selected || !current || selected === current) return;
      const operationScope = 'artifact-restore';
      const detail = `${selected}:${current}`;
      const identity = this.captureMutationIdentity();
      const idempotencyKey = this.operationKey(operationScope, detail, identity);
      this.mutationBusy = true;
      this.error = '';
      try {
        const response = await this.fetchMutationWithReconciliation(`/chat/document-artifacts/${encodeURIComponent(identity.resourceId)}/restore`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            chat_uuid: identity.chatUuid,
            source_version: selected,
            expected_current_version: current,
            idempotency_key: idempotencyKey,
          }),
        }, identity);
        const data = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(data.message || 'No se pudo restaurar la versión.');
        this.releaseOperationKey(operationScope, detail, identity);
        if (!this.isCurrentMutationIdentity(identity)) return;
        const restoredVersion = Number(data && data.version);
        if (Number.isInteger(restoredVersion) && restoredVersion > 0) {
          this.artifactVersionOverride = restoredVersion;
        } else {
          // A successful restore must never reload the receipt's older pin.
          this.artifactVersionOverride = 0;
        }
        await this.load();
      } catch (error) {
        if (this.isCurrentMutationIdentity(identity)) {
          this.error = String(error && error.message ? error.message : 'No se pudo restaurar la versión.');
        }
      } finally {
        if (this.isCurrentMutationIdentity(identity)) this.mutationBusy = false;
      }
    },
    formatReviewDate(value) {
      const raw = String(value == null ? '' : value).trim();
      if (!raw) return '';
      const parsed = new Date(raw);
      if (Number.isNaN(parsed.getTime())) return raw.slice(0, 80);
      try {
        return new Intl.DateTimeFormat('es', {
          dateStyle: 'medium',
          timeStyle: 'short',
        }).format(parsed);
      } catch (error) {
        return raw.slice(0, 80);
      }
    },
    setReactiveMapValue(mapName, key, value) {
      const target = this[mapName];
      if (!target || !key) return;
      if (typeof this.$set === 'function') this.$set(target, key, value);
      else target[key] = value;
    },
    safeLocatorParts(locator) {
      if (!locator || typeof locator !== 'object' || Array.isArray(locator)) return [];
      const allowed = [
        ['page', 'Página'],
        ['pages', 'Páginas'],
        ['pagina', 'Página'],
        ['article', 'Artículo'],
        ['articulo', 'Artículo'],
        ['paragraph', 'Párrafo'],
        ['parrafo', 'Párrafo'],
        ['section', 'Sección'],
        ['seccion', 'Sección'],
        ['clause', 'Cláusula'],
        ['clausula', 'Cláusula'],
        ['block', 'Bloque'],
        ['bloque', 'Bloque'],
        ['chunk', 'Fragmento'],
        ['court', 'Tribunal'],
        ['tribunal', 'Tribunal'],
        ['chamber', 'Sala'],
        ['sala', 'Sala'],
        ['date', 'Fecha'],
        ['fecha', 'Fecha'],
        ['decision_number', 'Decisión'],
        ['effective_date', 'Vigencia'],
        ['jurisdiction', 'Jurisdicción'],
      ];
      const seen = new Set();
      const parts = [];
      allowed.forEach(([key, label]) => {
        if (seen.has(label) || !Object.prototype.hasOwnProperty.call(locator, key)) return;
        const rawValue = locator[key];
        const displayValue = Array.isArray(rawValue)
          ? rawValue
            .filter(value => ['string', 'number'].includes(typeof value))
            .slice(0, 20)
            .join(', ')
          : rawValue;
        if (!['string', 'number'].includes(typeof displayValue)) return;
        const value = String(displayValue).replace(/[\u0000-\u001f\u007f]/g, '').trim().slice(0, 160);
        if (!value) return;
        seen.add(label);
        parts.push({ label, value });
      });
      return parts;
    },
    locatorText(locator) {
      return this.safeLocatorParts(locator)
        .map(item => `${item.label} ${item.value}`)
        .join(' · ');
    },
    sourceMatchLabel(value) {
      return ({
        exact_phrase: 'Coincidencia exacta',
        heading: 'Encabezado',
        high_overlap: 'Coincidencia alta',
        weak_overlap: 'Coincidencia débil',
        range: 'Rango leído',
      })[String(value || '').trim()] || '';
    },
    sourceMatchTone(value) {
      const matchKind = String(value || '').trim();
      if (['exact_phrase', 'heading', 'range', 'high_overlap'].includes(matchKind)) return 'verified';
      if (matchKind === 'weak_overlap') return 'attention';
      return 'neutral';
    },
    sourcePassageKey(passage, index) {
      return String(passage && passage.anchor_id || `passage-${index}`);
    },
    async copySourcePassage(passage, index) {
      const key = this.sourcePassageKey(passage, index);
      const quote = String(passage && passage.quote || '').trim();
      if (!quote) return;
      this.copyPassageErrorKey = '';
      try {
        if (!navigator.clipboard || typeof navigator.clipboard.writeText !== 'function') {
          throw new Error('clipboard_unavailable');
        }
        await navigator.clipboard.writeText(quote);
        this.copiedPassageKey = key;
        if (this.copyPassageTimer) window.clearTimeout(this.copyPassageTimer);
        this.copyPassageTimer = window.setTimeout(() => {
          if (this.copiedPassageKey === key) this.copiedPassageKey = '';
          this.copyPassageTimer = null;
        }, 2200);
      } catch (error) {
        this.copiedPassageKey = '';
        this.copyPassageErrorKey = key;
      }
    },
    safeCitationParts(citation) {
      if (!citation || typeof citation !== 'object' || Array.isArray(citation)) return [];
      const allowed = [
        ['code', 'Cita'],
        ['court', 'Tribunal'],
        ['date', 'Fecha'],
        ['decision_number', 'Decisión'],
        ['article', 'Artículo'],
        ['effective_date', 'Vigencia'],
        ['jurisdiction', 'Jurisdicción'],
      ];
      return allowed.flatMap(([key, label]) => {
        const rawValue = citation[key];
        if (!['string', 'number'].includes(typeof rawValue)) return [];
        const value = String(rawValue).replace(/[\u0000-\u001f\u007f]/g, '').trim().slice(0, 300);
        return value ? [{ label, value }] : [];
      });
    },
    legalSourceMetadata(basis) {
      if (!basis || typeof basis !== 'object') return '';
      const parts = this.safeLocatorParts(basis.locator);
      const labels = new Set(parts.map(item => item.label));
      this.safeCitationParts(basis.citation).forEach((item) => {
        if (!labels.has(item.label)) {
          labels.add(item.label);
          parts.push(item);
        }
      });
      return parts.map(item => `${item.label} ${item.value}`).join(' · ');
    },
    evidenceAnchorId(evidence) {
      if (typeof evidence === 'string') return evidence.trim();
      if (!evidence || typeof evidence !== 'object') return '';
      return String(
        evidence.anchor_id
        || evidence.document_anchor_id
        || evidence.source_anchor_id
        || '',
      ).trim();
    },
    evidenceOccurrenceKey(scope, ownerId, index, evidence) {
      const anchorId = this.evidenceAnchorId(evidence);
      if (!anchorId) return '';
      const safeIndex = Number.isInteger(index) ? String(index) : String(index == null ? '' : index);
      return [
        String(scope || 'source'),
        String(ownerId || ''),
        safeIndex,
        anchorId,
      ].join('|');
    },
    exactSourceInstanceKey(evidence, occurrenceKey, originElement) {
      const fromArgument = String(occurrenceKey == null ? '' : occurrenceKey).trim();
      if (fromArgument) return fromArgument;
      const fromElement = originElement && typeof originElement.getAttribute === 'function'
        ? String(originElement.getAttribute('data-source-occurrence-key') || '').trim()
        : '';
      return fromElement || this.evidenceAnchorId(evidence);
    },
    evidenceAspects(evidence) {
      if (!evidence || typeof evidence !== 'object') return [];
      const values = evidence.aspects || evidence.reviewed_aspects || evidence.aspectos;
      return Array.isArray(values)
        ? values
          .filter(value => typeof value === 'string' && value.trim())
          .map(value => value.replace(/_/g, ' '))
        : [];
    },
    findingDocumentEvidence(finding) {
      const envelope = finding && finding.evidence;
      if (!envelope || typeof envelope !== 'object') return [];
      if (Array.isArray(envelope.document_evidence)) {
        const values = envelope.document_evidence.filter(item => (
          item && typeof item === 'object' && (this.evidenceAnchorId(item) || item.quote)
        ));
        if (values.length) return values;
      }
      return this.evidenceAnchorId(envelope) || envelope.quote
        ? [{ ...envelope, role: envelope.role || 'claim' }]
        : [];
    },
    evidenceRoleLabel(role) {
      const labels = {
        claim: 'Pasaje observado',
        conflicts_with: 'Pasaje en conflicto',
        reference_target: 'Destino de la referencia',
        coverage: 'Prueba de cobertura',
      };
      return labels[String(role || 'claim').toLowerCase()] || 'Evidencia documental';
    },
    exactAnchorProjection(anchorId) {
      return this.anchorEvidenceById[String(anchorId || '')] || null;
    },
    exactAnchorPassage(anchorId) {
      const projection = this.exactAnchorProjection(anchorId);
      if (!projection) return null;
      if (Array.isArray(projection.passages)) {
        return projection.passages.find(item => item && typeof item === 'object') || null;
      }
      return projection;
    },
    exactAnchorQuote(anchorId) {
      const passage = this.exactAnchorPassage(anchorId);
      return String(passage && passage.quote || '');
    },
    exactAnchorLocator(anchorId) {
      const passage = this.exactAnchorPassage(anchorId);
      return this.locatorText(passage && passage.locator);
    },
    isAnchorOpen(anchorId) {
      return this.openAnchorIds[String(anchorId || '')] === true;
    },
    isAnchorLoading(anchorId) {
      return this.loadingAnchorIds[String(anchorId || '')] === true;
    },
    anchorLoadError(anchorId) {
      return String(this.anchorLoadErrors && this.anchorLoadErrors[String(anchorId || '')] || '');
    },
    collapseOpenExactSources() {
      const openIds = this.openAnchorIds;
      if (!openIds || typeof openIds !== 'object') {
        this.openAnchorIds = {};
        return;
      }
      const hasOpenPassage = Object.keys(openIds).some(id => openIds[id] === true);
      if (!hasOpenPassage) return;
      this.openAnchorIds = {};
    },
    exactSourceRevealOriginCard(originElement) {
      if (!originElement || typeof originElement.closest !== 'function') return null;
      return originElement.closest('.tool-review-source-card');
    },
    revealExactSource(anchorId, originElement, occurrenceKey) {
      if (!this.$nextTick) return;
      this.$nextTick(() => {
        const content = this.$refs && this.$refs.content;
        if (!content || typeof content.querySelectorAll !== 'function') return;
        const regions = Array.from(content.querySelectorAll('[data-source-anchor-id]'));
        const instanceKey = String(occurrenceKey || '').trim();
        const matchingOccurrence = instanceKey
          ? regions.find(element => (
            String(element.dataset && element.dataset.sourceOccurrenceKey || '') === instanceKey
          ))
          : null;
        const matchingRegions = regions.filter(element => (
          String(element.dataset && element.dataset.sourceAnchorId || '') === String(anchorId || '')
        ));
        const originCard = this.exactSourceRevealOriginCard(originElement);
        const region = matchingOccurrence || (
          originCard
          && matchingRegions.find(element => (
            typeof originCard.contains === 'function' && originCard.contains(element)
          ))
        ) || matchingRegions[0];
        if (!region) return;
        const target = region.querySelector('[data-source-passage-highlight]') || region;
        if (!target || typeof target.scrollIntoView !== 'function') return;
        const reduceMotion = typeof window !== 'undefined'
          && typeof window.matchMedia === 'function'
          && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        target.scrollIntoView({
          behavior: reduceMotion ? 'auto' : 'smooth',
          block: 'center',
          inline: 'nearest',
        });
      });
    },
    async toggleExactSource(evidence, event, occurrenceKey) {
      const originElement = event && (event.currentTarget || event.target);
      const instanceKey = this.exactSourceInstanceKey(evidence, occurrenceKey, originElement);
      const anchorId = this.evidenceAnchorId(evidence);
      if (!anchorId || !instanceKey || this.isAnchorLoading(instanceKey)) return;
      if (this.isAnchorOpen(instanceKey)) {
        this.setReactiveMapValue('openAnchorIds', instanceKey, false);
        if (this.pendingExactSourceKey === instanceKey) this.pendingExactSourceKey = '';
        return;
      }
      this.pendingExactSourceKey = instanceKey;
      this.collapseOpenExactSources();
      if (this.exactAnchorProjection(anchorId)) {
        this.setReactiveMapValue('openAnchorIds', instanceKey, true);
        this.revealExactSource(anchorId, originElement, instanceKey);
        return;
      }
      const receiptId = String(this.receipt && this.receipt.tool_run_id || '');
      this.setReactiveMapValue('loadingAnchorIds', instanceKey, true);
      this.setReactiveMapValue('anchorLoadErrors', instanceKey, '');
      try {
        const query = new URLSearchParams({ chat_uuid: this.chatUuid });
        const response = await fetch(`/chat/source-anchors/${encodeURIComponent(anchorId)}?${query.toString()}`);
        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw createToolResultAccessError(toolResultAccessFeedback({
            response,
            resultType: 'anchor_set',
            retryable: data.retryable === true,
          }));
        }
        if (String(this.receipt && this.receipt.tool_run_id || '') !== receiptId) return;
        if (this.pendingExactSourceKey !== instanceKey) return;
        this.collapseOpenExactSources();
        this.setReactiveMapValue('anchorEvidenceById', anchorId, data);
        this.setReactiveMapValue('openAnchorIds', instanceKey, true);
        this.revealExactSource(anchorId, originElement, instanceKey);
      } catch (error) {
        if (
          String(this.receipt && this.receipt.tool_run_id || '') === receiptId
          && this.pendingExactSourceKey === instanceKey
        ) {
          const feedback = toolResultAccessFeedback({
            error,
            resultType: 'anchor_set',
          });
          this.setReactiveMapValue(
            'anchorLoadErrors',
            instanceKey,
            `${feedback.title}. ${feedback.message}`,
          );
        }
      } finally {
        if (String(this.receipt && this.receipt.tool_run_id || '') === receiptId) {
          this.setReactiveMapValue('loadingAnchorIds', instanceKey, false);
        }
      }
    },
    findingStateVersion(finding) {
      const version = Number(
        finding && (finding.finding_state_version ?? finding.state_version),
      );
      return Number.isInteger(version) && version >= 1 ? version : 1;
    },
    dispositionReasonDraft(finding) {
      const findingId = String(finding && finding.finding_id || '');
      if (!findingId) return '';
      if (Object.prototype.hasOwnProperty.call(this.dispositionReasonDrafts, findingId)) {
        return String(this.dispositionReasonDrafts[findingId] || '');
      }
      return String(finding.disposition_reason || finding.reason || '').slice(0, 500);
    },
    setDispositionReasonDraft(finding, value) {
      const findingId = String(finding && finding.finding_id || '');
      if (!findingId) return;
      this.setReactiveMapValue(
        'dispositionReasonDrafts',
        findingId,
        String(value == null ? '' : value).slice(0, 500),
      );
    },
    replaceFindingProjection(findingId, updatedFinding) {
      if (!this.projection || !Array.isArray(this.projection.findings)) return;
      const index = this.projection.findings.findIndex(item => (
        String(item && item.finding_id || '') === String(findingId || '')
      ));
      if (index < 0 || !updatedFinding || typeof updatedFinding !== 'object') return;
      const findings = this.projection.findings.slice();
      findings.splice(index, 1, { ...findings[index], ...updatedFinding });
      this.projection = { ...this.projection, findings };
    },
    findingCompletion(finding) {
      return reviewFindingCompletion(finding, {
        isEditable: this.reviewIsEditable,
        usesConsiderActions: this.reviewUsesConsiderActions,
      });
    },
    reviewFindingActionDetail(finding, action) {
      if (!action || action.disposition !== 'accepted' || this.reviewUsesConsiderActions) {
        return String(action && action.detail || '');
      }
      const applicationStatus = findingApplicationStatus(finding);
      if (applicationStatus === 'partially_applied') {
        return 'Aplicado parcialmente con marcadores pendientes';
      }
      if (applicationStatus === 'applied') {
        return 'Ya se aplicó al documento';
      }
      if (findingHasAppliedChanges(finding)) {
        if (findingHasPartialApplicationHistory(finding)) {
          return 'Aplicado parcialmente antes · pendiente de volver a aplicar';
        }
        return 'Pendiente de volver a aplicar';
      }
      return String(action.detail || '');
    },
    reopenFindingLabel(finding) {
      const disposition = String(finding && finding.disposition || 'open').toLowerCase();
      if (disposition === 'accepted') {
        return this.reviewUsesConsiderActions ? 'Dejar de considerar' : 'Quitar aprobación';
      }
      return 'Volver a dejar pendiente';
    },
    async requestAppliedFindingUnapproval(finding, disposition, event, identity) {
      const returnFocus = event && event.currentTarget;
      this.reviewDecisionConfirmationPending = true;
      let confirmed = false;
      try {
        confirmed = await confirmAppliedFindingUnapproval(finding, disposition);
      } finally {
        this.reviewDecisionConfirmationPending = false;
        if (!confirmed && returnFocus) {
          if (typeof this.$nextTick === 'function') await this.$nextTick();
          const isConnected = typeof document === 'undefined'
            || document.contains(returnFocus);
          if (
            isConnected
            && !returnFocus.disabled
            && typeof returnFocus.focus === 'function'
          ) {
            try {
              returnFocus.focus();
            } catch (error) {
              // The originating control may have disappeared with a concurrent refresh.
            }
          }
        }
      }
      return {
        confirmed: Boolean(confirmed && this.isCurrentMutationIdentity(identity)),
        returnFocus,
        appliedVersion: appliedFindingHistoryVersion(finding),
      };
    },
    async setRemainingFindingsDisposition(disposition) {
      if (
        !this.reviewIsEditable
        || this.mutationBusy
        || this.reviewDecisionConfirmationPending
        || !['accepted', 'dismissed'].includes(disposition)
      ) return;
      this.reviewApplyConfirmation = null;
      const targets = this.visibleOpenReviewFindings.slice();
      if (!targets.length) return;
      const findings = targets.map(finding => ({
        finding_id: String(finding.finding_id || ''),
        expected_state_version: this.findingStateVersion(finding),
      })).filter(finding => finding.finding_id);
      if (!findings.length) return;

      const operationScope = 'review-bulk-disposition';
      const detail = JSON.stringify({ disposition, findings });
      const identity = this.captureMutationIdentity();
      const idempotencyKey = this.operationKey(operationScope, detail, identity);
      this.mutationBusy = true;
      this.savedFindingId = '';
      this.error = '';
      try {
        const response = await this.fetchMutationWithReconciliation(`/chat/document-reviews/${encodeURIComponent(identity.resourceId)}/findings/bulk-disposition`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            chat_uuid: identity.chatUuid,
            disposition,
            findings,
            idempotency_key: idempotencyKey,
          }),
        }, identity);
        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(data.message || 'No se pudieron guardar las decisiones restantes.');
        }
        this.releaseOperationKey(operationScope, detail, identity);
        if (!this.isCurrentMutationIdentity(identity)) return;
        await this.load({ silent: true, preserveReviewOrder: true });
        if (!this.isCurrentMutationIdentity(identity)) return;
        if (typeof this.$emit === 'function') {
          this.$emit('review-state-changed', data.active_review || null);
        }
      } catch (error) {
        if (this.isCurrentMutationIdentity(identity)) {
          this.error = String(
            error && error.message
              ? error.message
              : 'No se pudieron guardar las decisiones restantes.',
          );
        }
      } finally {
        if (this.isCurrentMutationIdentity(identity)) this.mutationBusy = false;
      }
    },
    async setFindingDisposition(finding, disposition, event = null) {
      if (
        !finding
        || !finding.finding_id
        || this.mutationBusy
        || this.reviewDecisionConfirmationPending
      ) return;
      if (!['open', 'accepted', 'intentional', 'dismissed'].includes(disposition)) return;
      const allowedDispositions = Array.isArray(this.reviewAllowedDispositions)
        ? this.reviewAllowedDispositions
        : ['open', 'accepted', 'intentional', 'dismissed'];
      if (!allowedDispositions.includes(disposition)) return;
      if (String(finding.disposition || 'open') === disposition) return;

      const findingId = String(finding.finding_id);
      const shouldShowPendingDecisionReceipt = Boolean(
        this.reviewIsPendingFilter
        && String(finding.disposition || 'open').toLowerCase() === 'open'
        && disposition !== 'open'
      );
      const identity = this.captureMutationIdentity();
      let confirmedUnapprovalReturnFocus = null;
      let confirmedAppliedVersion = 0;
      if (
        this.reviewIsEditable
        && String(finding.disposition || 'open').toLowerCase() === 'accepted'
        && disposition !== 'accepted'
        && findingHasAppliedChanges(finding)
      ) {
        const confirmation = await this.requestAppliedFindingUnapproval(
          finding,
          disposition,
          event,
          identity,
        );
        if (!confirmation.confirmed) return;
        const latestFinding = this.findings.find(item => (
          String(item && item.finding_id || '') === findingId
        ));
        if (
          !latestFinding
          || String(latestFinding.disposition || 'open').toLowerCase() !== 'accepted'
        ) return;
        finding = latestFinding;
        confirmedAppliedVersion = confirmation.appliedVersion;
        confirmedUnapprovalReturnFocus = confirmation.returnFocus;
      }

      this.reviewApplyConfirmation = null;
      const operationScope = 'finding-disposition';
      const expectedStateVersion = this.findingStateVersion(finding);
      const reason = this.dispositionReasonDraft(finding).trim();
      const detail = `${findingId}:${expectedStateVersion}:${disposition}:${reason}`;
      const idempotencyKey = this.operationKey(operationScope, detail, identity);
      const viewportAnchor = this.captureReviewViewportAnchor(
        findingId,
        event && event.currentTarget,
      );
      this.activeFindingMutationId = findingId;
      this.activeReviewViewportAnchor = viewportAnchor;
      this.savedFindingId = '';
      this.mutationBusy = true;
      this.error = '';
      this.restoreReviewViewportAnchor(viewportAnchor);
      try {
        let response = null;
        let data = {};
        for (let attempt = 0; attempt < 2; attempt += 1) {
          response = await this.fetchMutationWithReconciliation(`/chat/document-reviews/${encodeURIComponent(identity.resourceId)}/findings/${encodeURIComponent(findingId)}/disposition`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              chat_uuid: identity.chatUuid,
              disposition,
              reason,
              expected_state_version: expectedStateVersion,
              confirmed_applied_finding_state_version: confirmedAppliedVersion,
              idempotency_key: idempotencyKey,
            }),
          }, identity);
          data = await response.json().catch(() => ({}));
          const needsAppliedConfirmation = Boolean(
            !response.ok
            && data.code === 'review_applied_unapproval_confirmation_required'
            && data.finding
            && typeof data.finding === 'object'
            && attempt === 0
          );
          if (!needsAppliedConfirmation) break;
          if (this.isCurrentMutationIdentity(identity) && data.finding) {
            this.replaceFindingProjection(findingId, data.finding);
          }
          if (!this.isCurrentMutationIdentity(identity)) return;
          const latestFinding = this.findings.find(item => (
            String(item && item.finding_id || '') === findingId
          ));
          if (
            !latestFinding
            || String(latestFinding.disposition || 'open').toLowerCase() !== 'accepted'
            || !findingHasAppliedChanges(latestFinding)
          ) break;
          this.mutationBusy = false;
          const confirmation = await this.requestAppliedFindingUnapproval(
            latestFinding,
            disposition,
            event,
            identity,
          );
          if (!confirmation.confirmed) return;
          confirmedAppliedVersion = confirmation.appliedVersion;
          confirmedUnapprovalReturnFocus = confirmation.returnFocus;
          this.mutationBusy = true;
        }
        if (!response || !response.ok) {
          throw new Error(data.message || 'No se pudo actualizar la observación.');
        }
        this.releaseOperationKey(operationScope, detail, identity);
        if (!this.isCurrentMutationIdentity(identity)) return;
        const updatedFinding = data.finding && typeof data.finding === 'object'
          ? data.finding
          : {
            ...finding,
            disposition: String(data.disposition || disposition),
            disposition_reason: reason,
            finding_state_version: Number(data.finding_state_version || expectedStateVersion + 1),
          };
        this.replaceFindingProjection(findingId, updatedFinding);
        this.setDispositionReasonDraft(updatedFinding, updatedFinding.disposition_reason || reason);
        this.savedFindingId = findingId;
        if (
          shouldShowPendingDecisionReceipt
          && typeof this.recordPendingReviewDecisionReceipt === 'function'
        ) {
          this.recordPendingReviewDecisionReceipt(updatedFinding);
          if (typeof this.focusReviewTarget === 'function') {
            this.focusReviewTarget(findingId);
          }
        }
        this.restoreReviewViewportAnchor(viewportAnchor);
        this.preparePendingReviewView();
        if (typeof this.$emit === 'function') {
          this.$emit('review-state-changed', data.active_review || null);
        }
      } catch (error) {
        if (this.isCurrentMutationIdentity(identity)) {
          this.error = String(error && error.message ? error.message : 'No se pudo actualizar la observación.');
        }
      } finally {
        if (this.isCurrentMutationIdentity(identity)) {
          this.mutationBusy = false;
          this.activeFindingMutationId = '';
          this.activeReviewViewportAnchor = null;
          this.restoreReviewViewportAnchor(viewportAnchor);
          if (confirmedUnapprovalReturnFocus) {
            if (typeof this.$nextTick === 'function') await this.$nextTick();
            const originalIsAvailable = (
              (typeof document === 'undefined'
                || document.contains(confirmedUnapprovalReturnFocus))
              && !confirmedUnapprovalReturnFocus.disabled
            );
            const findingElement = typeof this.reviewFindingElement === 'function'
              ? this.reviewFindingElement(findingId)
              : null;
            const focusTarget = originalIsAvailable
              ? confirmedUnapprovalReturnFocus
              : (
                findingElement
                && findingElement.querySelector('.tool-finding__actions button:not([disabled])')
              ) || (this.$refs && this.$refs.panel);
            if (focusTarget && typeof focusTarget.focus === 'function') {
              try {
                focusTarget.focus();
              } catch (error) {
                // A concurrent refresh may remove the selected fallback target.
              }
            }
          }
        }
      }
    },
    openContextUpload() {
      if (!this.isContextRequest || this.uploadingContextDocument || this.mutationBusy) return;
      const input = this.$refs.contextUploadInput;
      if (!input || typeof input.click !== 'function') return;
      input.value = '';
      input.click();
    },
    contextUploadMediaFlags(file) {
      const mimeType = String(file && file.type || '').trim().toLocaleLowerCase();
      const filename = String(file && file.name || '').trim().toLocaleLowerCase();
      const extensionMatch = filename.match(/\.([a-z0-9]+)$/);
      const extension = extensionMatch ? extensionMatch[1] : '';
      const videoExtensions = new Set(['3g2', '3gp', 'avi', 'm4v', 'mkv', 'mov', 'mp4', 'mpeg', 'mpg', 'webm', 'wmv']);
      const audioExtensions = new Set(['aac', 'aiff', 'flac', 'm4a', 'mp3', 'oga', 'ogg', 'opus', 'wav', 'wma']);
      if (mimeType.startsWith('video/') || videoExtensions.has(extension)) {
        return { isAudio: false, isVideo: true };
      }
      if (mimeType.startsWith('audio/') || audioExtensions.has(extension)) {
        return { isAudio: true, isVideo: false };
      }
      return { isAudio: false, isVideo: false };
    },
    async resolveUploadedContextSource(uploadHref, identity) {
      const requestId = String(identity && identity.resourceId || '');
      const path = String(uploadHref || '').trim();
      if (!requestId || !path) throw new Error('No se pudo identificar el documento subido.');
      const response = await fetch(
        `/chat/context-requests/${encodeURIComponent(requestId)}/sources/resolve-upload`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            chat_uuid: identity.chatUuid,
            path,
            is_user_document: true,
            expected_resolution_epoch: identity.resolutionEpoch,
          }),
        },
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.message || 'No se pudo verificar el documento subido.');
      if (!this.isCurrentMutationIdentity(identity)) return null;
      const returnedEpoch = Number.parseInt(String(data.resolution_epoch), 10);
      if (
        Number.isInteger(returnedEpoch)
        && returnedEpoch >= 0
        && returnedEpoch !== identity.resolutionEpoch
      ) throw new Error('La solicitud cambió mientras se verificaba el documento. Vuelve a abrirla.');
      const sourceId = String(data && data.source_id || '').trim();
      if (!sourceId) throw new Error('No se pudo identificar el documento subido.');
      return {
        source_id: sourceId,
        title: String(data && data.title || 'Documento subido').slice(0, 300),
        scope_kind: String(data && data.scope_kind || '').slice(0, 32),
        source_kind: String(data && data.source_kind || '').slice(0, 64),
      };
    },
    async uploadContextDocument(event) {
      const input = event && event.target;
      const file = input && input.files && input.files[0];
      if (!file || !this.isContextRequest || this.uploadingContextDocument) return;
      if (typeof window.finalizeFileUpload !== 'function') {
        this.error = 'El cargador de documentos no está disponible. Inténtalo de nuevo desde Mis archivos.';
        return;
      }
      const identity = this.captureMutationIdentity();
      const mediaFlags = this.contextUploadMediaFlags(file);
      this.uploadingContextDocument = true;
      this.contextUploadMessage = `Subiendo ${file.name}…`;
      this.error = '';
      try {
        const uploadHref = await new Promise((resolve, reject) => {
          window.finalizeFileUpload(
            [file],
            mediaFlags.isAudio,
            mediaFlags.isVideo,
            false,
            false,
            (href, uploadError) => {
              if (uploadError) {
                reject(new Error('No se pudo procesar el documento subido.'));
                return;
              }
              resolve(href);
            },
            'progressBarToolContext',
            'progressPercentageToolContext',
            'postUploadToolContext',
            'personal',
          );
        });
        if (!this.isCurrentMutationIdentity(identity)) return;
        this.contextUploadMessage = 'Documento listo. Verificando en tu biblioteca…';
        const uploadedSource = await this.resolveUploadedContextSource(uploadHref, identity);
        if (!uploadedSource || !this.isCurrentMutationIdentity(identity)) return;
        const existingIndex = this.contextSources.findIndex((source) => (
          String(source && source.source_id || '') === uploadedSource.source_id
        ));
        if (existingIndex >= 0) {
          this.contextSources = this.contextSources.map((source, index) => (
            index === existingIndex ? uploadedSource : source
          ));
        } else {
          this.contextSources = [uploadedSource].concat(this.contextSources);
        }
        if (uploadedSource && this.selectedSourceIds.length < this.maxContextSelectionSources) {
          const sourceId = String(uploadedSource.source_id || '');
          const isCompatible = !this.structuredRequestItems.length
            || this.compatibleRequestItems(uploadedSource).length > 0;
          if (sourceId && isCompatible && !this.selectedSourceIds.includes(sourceId)) {
            this.selectedSourceIds = this.selectedSourceIds.concat(sourceId);
            this.assignContextSourceRequestItem(sourceId, uploadedSource);
          } else if (sourceId && !isCompatible) {
            this.error = 'El archivo se subió, pero su tipo no es aceptado para esta solicitud.';
          }
        }
      } catch (error) {
        if (!this.isCurrentMutationIdentity(identity)) return;
        this.error = String(error && error.message ? error.message : 'No se pudo subir el documento.');
      } finally {
        if (this.isCurrentMutationIdentity(identity)) {
          this.uploadingContextDocument = false;
          this.contextUploadMessage = '';
        }
      }
    },
    severityLabel(severity) {
      const labels = {
        high: 'Alto',
        medium: 'Medio',
        low: 'Bajo',
        info: 'Informativa',
      };
      const key = String(severity || '').toLowerCase();
      return labels[key] || 'Sin clasificar';
    },
    classificationLabel(classification) {
      const confirmed = String(classification || '').toLowerCase() === 'confirmed';
      if (this && this.reviewIsInstitutional) {
        return confirmed ? 'Señal respaldada por evidencia' : 'Posible incidencia por verificar';
      }
      return confirmed ? 'Confirmado con evidencia' : 'Posible riesgo';
    },
    classificationClass(classification) {
      return String(classification || '').toLowerCase() === 'confirmed'
        ? 'tool-finding__badge--classification-confirmed'
        : 'tool-finding__badge--classification-potential';
    },
    severityClass(severity) {
      const value = String(severity || '').toLowerCase();
      return ['high', 'medium', 'low'].includes(value)
        ? `tool-finding__badge--severity-${value}`
        : 'tool-finding__badge--severity-unknown';
    },
    dispositionLabel(disposition) {
      if (this && this.reviewUsesConsiderActions) {
        const considerationLabels = {
          open: 'Pendiente de evaluación',
          intentional: 'Marcado previamente como intencional',
          dismissed: 'Descartado',
          accepted: 'Hallazgo considerado',
          apply: 'Hallazgo considerado',
        };
        return considerationLabels[String(disposition || 'open').toLowerCase()]
          || 'Estado sin clasificar';
      }
      const labels = {
        open: 'Pendiente de decisión',
        intentional: 'Marcado como intencional',
        dismissed: 'Descartado',
        accepted: 'Recomendación aprobada',
        apply: 'Aprobado para aplicar',
      };
      return labels[String(disposition || 'open').toLowerCase()] || 'Estado sin clasificar';
    },
    dispositionClass(disposition) {
      const value = String(disposition || 'open').toLowerCase();
      return ['open', 'intentional', 'dismissed', 'accepted', 'apply'].includes(value)
        ? `tool-finding__badge--disposition-${value}`
        : 'tool-finding__badge--disposition-unknown';
    },
    dispositionAuditText(finding) {
      if (!finding || typeof finding !== 'object') return '';
      return this.formatReviewDate(finding.disposition_at || finding.updated_at);
    },
    close(options = {}) {
      if (
        (this.mutationBusy || this.reviewDecisionConfirmationPending)
        && options.allowDuringMutation !== true
      ) return;
      this.disposeReviewHelpPopover();
      this.$emit('close');
    },
    onKeydown(event) {
      if (!this.isOpen) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        if (this.reviewDecisionConfirmationPending) return;
        if (this.reviewApplyConfirmation) {
          this.dismissReviewApplyConfirmation();
          return;
        }
        this.close();
        return;
      }
      if (event.key !== 'Tab' || !this.$refs.panel) return;
      const focusable = Array.from(this.$refs.panel.querySelectorAll([
        'button:not([disabled])',
        '[href]',
        'input:not([disabled]):not([type="hidden"])',
        'select:not([disabled])',
        'textarea:not([disabled])',
        'summary',
        '[tabindex]:not([tabindex="-1"])',
      ].join(', '))).filter(element => (
        element.getClientRects().length > 0
        && element.getAttribute('aria-hidden') !== 'true'
      ));
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault(); last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault(); first.focus();
      }
    },
    async copyArtifact() {
      if (this.artifactProvenanceUnavailable) return;
      const plainText = this.plainArtifactText;
      const html = this.renderedArtifact;
      if (!plainText) return;
      try {
        if (
          window.ClipboardItem
          && navigator.clipboard
          && typeof navigator.clipboard.write === 'function'
        ) {
          await navigator.clipboard.write([new ClipboardItem({
            'text/plain': new Blob([plainText], { type: 'text/plain' }),
            'text/html': new Blob([html], { type: 'text/html' }),
          })]);
          return;
        }
        if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
          await navigator.clipboard.writeText(plainText);
          return;
        }
        throw new Error('clipboard_unavailable');
      } catch (error) {
        this.error = 'No se pudo copiar el documento. Inténtalo de nuevo.';
      }
    },
    async copyArtifactMarkdown() {
      if (this.artifactProvenanceUnavailable) return;
      const content = this.projection && this.projection.content_markdown;
      if (!content) return;
      try {
        if (!navigator.clipboard || typeof navigator.clipboard.writeText !== 'function') {
          throw new Error('clipboard_unavailable');
        }
        await navigator.clipboard.writeText(String(content));
      } catch (error) {
        this.error = 'No se pudo copiar el Markdown. Inténtalo de nuevo.';
      }
    },
    downloadArtifact() {
      if (this.artifactProvenanceUnavailable) return;
      const content = this.projection && this.projection.content_markdown;
      if (!content) return;
      const blob = new Blob([String(content)], { type: 'text/markdown;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = String(this.projection.filename || 'documento.md').replace(/[^\w\-. áéíóúüñ]/gi, '-');
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    },
    async downloadExport(format) {
      if (
        !this.projection
        || this.artifactProvenanceUnavailable
        || !this.resultRef
        || !['pdf', 'docx'].includes(format)
      ) return;
      this.error = '';
      this.loadFailure = null;
      this.loadRetryable = false;
      try {
        const version = Number(this.projection.selected_version || this.requestedArtifactVersion || 0);
        if (!version) {
          this.error = 'No encontramos una versión disponible para descargar.';
          return;
        }
        const query = new URLSearchParams({ chat_uuid: this.chatUuid, format });
        const response = await fetch(`/chat/document-artifacts/${encodeURIComponent(String(this.resultRef.id))}/versions/${version}/export?${query.toString()}`);
        if (!response.ok) {
          const data = await response.json().catch(() => ({}));
          throw createToolResultAccessError(toolResultAccessFeedback({
            response,
            resultType: 'document',
            retryable: data.retryable === true,
          }));
        }
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        const stem = String(this.projection.filename || 'documento.md').replace(/\.md$/i, '');
        link.href = url;
        link.download = `${stem}.${format}`.replace(/[^\w\-. áéíóúüñ]/gi, '-');
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(url);
      } catch (error) {
        const feedback = toolResultAccessFeedback({ error, resultType: 'document' });
        this.error = `${feedback.title}. ${feedback.message}`;
      }
    },
  },
  template: `
    <div v-if="isOpen" class="tool-workspace" @mousedown.self="close">
      <aside
        ref="panel"
        class="tool-workspace__panel"
        :class="workspaceTypeClass"
        role="dialog"
        :aria-modal="reviewDecisionConfirmationPending ? 'false' : 'true'"
        :aria-hidden="reviewDecisionConfirmationPending ? 'true' : null"
        :inert="reviewDecisionConfirmationPending ? '' : null"
        aria-labelledby="tool-workspace-title"
        :aria-describedby="workspaceIntro ? 'tool-workspace-intro' : null"
        tabindex="-1"
      >
        <header class="tool-workspace__header">
          <div class="tool-workspace__header-identity">
            <span class="tool-workspace__header-icon" aria-hidden="true"><i :class="workspaceIconClass"></i></span>
            <div>
              <span class="tool-workspace__eyebrow">{{ workspaceEyebrow }}</span>
              <h2 id="tool-workspace-title">{{ title }}</h2>
              <p v-if="workspaceIntro" id="tool-workspace-intro" class="tool-workspace__intro">{{ workspaceIntro }}</p>
            </div>
          </div>
          <div class="tool-workspace__header-actions">
            <button
              v-if="resultRef && resultRef.type === 'review'"
              ref="reviewHelpButton"
              type="button"
              class="tool-workspace__help help-button-top"
              data-toggle="popover"
              data-trigger="focus"
              data-placement="bottom"
              data-boundary="viewport"
              title="Cómo funciona la revisión"
              data-content="La revisión organiza los hallazgos, la evidencia y los fundamentos jurídicos para que puedas comprobar cada punto y decidir qué hacer. En documentos editables, solo se aplican las recomendaciones que apruebes; las fuentes jurídicas no se modifican."
              aria-label="Ayuda sobre la revisión de documentos"
            ><i class="anticon anticon-question" aria-hidden="true"></i></button>
            <button
              ref="closeButton"
              type="button"
              class="tool-workspace__close"
              :aria-label="isInlineSourceRead ? 'Volver al resumen' : 'Volver al chat'"
              :title="isInlineSourceRead ? 'Volver al resumen' : 'Volver al chat'"
              :disabled="mutationBusy || reviewDecisionConfirmationPending"
              @click="close"
            ><span class="tool-workspace__close-icon" aria-hidden="true">←</span><span class="tool-workspace__close-label tool-workspace__close-label--desktop">{{ isInlineSourceRead ? 'Volver al resumen' : 'Volver al chat' }}</span><span class="tool-workspace__close-label tool-workspace__close-label--mobile">Volver</span></button>
          </div>
        </header>
        <div ref="content" class="tool-workspace__content">
          <div v-if="loading" class="tool-workspace__state" role="status">Cargando…</div>
          <div
            v-else-if="error"
            class="tool-workspace__state tool-workspace__state--error"
            :class="[
              loadFailure ? 'tool-workspace__access-error' : '',
              loadFailure ? 'tool-workspace__access-error--' + loadFailure.kind : ''
            ]"
            role="alert"
          >
            <template v-if="loadFailure">
              <span class="tool-workspace__access-error-icon" aria-hidden="true"><i :class="loadFailure.iconClass"></i></span>
              <div class="tool-workspace__access-error-copy" data-testid="tool-result-access-error">
                <h3 data-testid="tool-result-access-error-title">{{ loadFailure.title }}</h3>
                <p>{{ loadFailure.message }}</p>
                <small>{{ loadFailure.reassurance }}</small>
              </div>
            </template>
            <p v-else>{{ error }}</p>
            <button v-if="loadRetryable" type="button" data-testid="tool-result-access-retry" @click="retryToolResultLoad">{{ loadFailure ? loadFailure.actionLabel : 'Reintentar' }}</button>
          </div>
          <section v-if="isContinuation" class="tool-context-picker" aria-label="Estado de la respuesta">
            <p>{{ receipt && receipt.ui_payload ? receipt.ui_payload.summary : 'La continuación está guardada en esta conversación.' }}</p>
            <div class="tool-context-picker__actions">
              <button v-if="continuationCanStop" type="button" :disabled="mutationBusy" @click="transitionContinuation('stop')">Detener</button>
              <button v-if="continuationCanRetry" type="button" :disabled="mutationBusy" @click="transitionContinuation('retry')">Reintentar respuesta</button>
            </div>
          </section>
          <section v-else-if="isContextRequest" class="tool-context-picker" aria-label="Elegir archivos de contexto">
            <div v-if="requestedDocuments.length" class="tool-context-picker__request" aria-labelledby="context-request-heading">
              <div class="tool-context-picker__section-heading">
                <span class="tool-context-picker__step" aria-hidden="true">1</span>
                <div>
                  <span class="tool-context-picker__kicker">Lo que hace falta</span>
                  <h3 id="context-request-heading">{{ requestedDocuments.length === 1 ? 'Archivo solicitado' : 'Archivos solicitados' }}</h3>
                </div>
              </div>
              <ul>
                <li
                  v-for="(request, requestIndex) in requestedDocuments"
                  :key="request.requestItemId || (request.label + '-' + requestIndex)"
                  :class="{ 'is-covered': request.requestItemId && coveredRequestItemIds.has(request.requestItemId) }"
                >
                  <span class="tool-context-picker__request-mark" aria-hidden="true">{{ request.requestItemId && coveredRequestItemIds.has(request.requestItemId) ? '✓' : '•' }}</span>
                  <div>
                    <div class="tool-context-picker__request-heading">
                      <strong>{{ request.label }}</strong>
                      <span :class="['tool-context-picker__priority', 'tool-context-picker__priority--' + request.priority]">
                        {{ request.priority === 'necesario' ? 'Necesario' : 'Recomendado' }}
                      </span>
                    </div>
                    <small v-if="request.detail">{{ request.detail }}</small>
                    <small class="tool-context-picker__accepted-types">
                      Formatos: {{ request.acceptedTypes.map(contextTypeLabel).join(', ') }}
                    </small>
                  </div>
                </li>
              </ul>
            </div>
            <div v-else class="tool-context-picker__request tool-context-picker__request--unstructured">
              <strong>{{ receiptSummary || 'Elige los archivos que ayudarán a completar esta respuesta.' }}</strong>
            </div>

            <div class="tool-context-picker__library" aria-labelledby="context-library-heading">
              <div class="tool-context-picker__section-heading">
                <span class="tool-context-picker__step" aria-hidden="true">2</span>
                <div>
                  <span class="tool-context-picker__kicker">Elige la fuente</span>
                  <h3 id="context-library-heading">Tu biblioteca</h3>
                </div>
                <span class="tool-context-picker__selection-count" role="status" aria-live="polite">{{ contextSelectionStatus }}</span>
              </div>
              <p class="tool-context-picker__privacy-note"><span aria-hidden="true">✓</span> Los archivos se usarán solo para responder esta solicitud.</p>
              <div class="tool-context-picker__source-tools">
                <form class="tool-context-picker__search" @submit.prevent="searchContextSources(true)">
                  <label for="tool-context-query">Buscar por nombre</label>
                  <div>
                    <input id="tool-context-query" v-model="contextQuery" type="search" maxlength="120" autocomplete="off" placeholder="Ej. contrato firmado" :disabled="mutationBusy">
                    <button type="submit" :disabled="loading || mutationBusy"><span class="anticon anticon-search" aria-hidden="true"></span><span>Buscar</span></button>
                  </div>
                </form>
                <span class="tool-context-picker__source-divider" aria-hidden="true">o</span>
                <button
                  type="button"
                  class="tool-context-picker__upload"
                  :disabled="uploadingContextDocument || mutationBusy"
                  @click="openContextUpload"
                ><span class="anticon anticon-upload" aria-hidden="true"></span>{{ uploadingContextDocument ? 'Subiendo archivo…' : 'Subir archivo nuevo' }}</button>
              </div>
            </div>
            <input
              ref="contextUploadInput"
              class="tool-context-picker__file-input"
              type="file"
              tabindex="-1"
              accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.heic,audio/*,video/*"
              @change="uploadContextDocument"
            >
            <div v-if="uploadingContextDocument" class="tool-context-picker__upload-status" role="status">
              <span>{{ contextUploadMessage }}</span>
              <div class="progress" aria-label="Progreso de carga">
                <div id="progressBarToolContext" class="progress-bar" role="progressbar" aria-valuemin="0" aria-valuemax="100" aria-valuenow="0">
                  <span id="progressPercentageToolContext">0</span>%
                </div>
              </div>
              <div id="postUploadToolContext" class="tool-context-picker__processing" style="display:none;">
                <span id="processingStatusToolContext">Procesando documento…</span>
              </div>
            </div>
            <div v-if="!loading && !contextSources.length" class="tool-context-picker__empty">
              <span class="anticon anticon-folder-open" aria-hidden="true"></span>
              <strong>No encontramos archivos</strong>
              <p>Prueba otra búsqueda o sube un archivo nuevo.</p>
            </div>
            <ul v-else class="tool-context-picker__list">
              <li
                v-for="source in contextSources"
                :key="source.source_id"
                :class="{
                  'is-selected': isContextSourceSelected(source),
                  'is-incompatible': !isContextSourceCompatible(source),
                }"
              >
                <label class="tool-context-picker__source-card">
                  <input
                    type="checkbox"
                    :checked="isContextSourceSelected(source)"
                    :disabled="mutationBusy || !isContextSourceCompatible(source) || (!isContextSourceSelected(source) && selectedSourceIds.length >= maxContextSelectionSources)"
                    @change="toggleContextSource(source.source_id)"
                  >
                  <span class="tool-context-picker__source-icon" aria-hidden="true"><i :class="contextSourceIconClass(source)"></i></span>
                  <span class="tool-context-picker__source-copy">
                    <strong>{{ source.title }}</strong>
                    <small>{{ source.scope_kind === 'corporate' ? 'Corporativo' : 'Personal' }} · {{ contextTypeLabel(contextSourceCategory(source)) }}</small>
                    <small v-if="contextSourceDisabledReason(source)" class="tool-context-picker__source-reason">{{ contextSourceDisabledReason(source) }}</small>
                  </span>
                  <span class="tool-context-picker__selected-mark" aria-hidden="true">✓</span>
                </label>
                <label v-if="isContextSourceSelected(source) && structuredRequestItems.length" class="tool-context-picker__mapping">
                  <span>Este archivo cubre</span>
                  <select
                    :value="sourceRequestItemIds[String(source.source_id)] || ''"
                    :disabled="mutationBusy"
                    :aria-label="'Solicitud cubierta por ' + source.title"
                    @change="setContextSourceRequestItem(source.source_id, $event.target.value)"
                  >
                    <option value="" disabled>Selecciona una solicitud</option>
                    <option
                      v-for="request in compatibleRequestItems(source)"
                      :key="request.requestItemId"
                      :value="request.requestItemId"
                    >{{ request.label }}{{ request.priority === 'necesario' ? ' (necesario)' : '' }}</option>
                  </select>
                </label>
              </li>
            </ul>
            <button v-if="contextCursor" type="button" class="tool-context-picker__more" :disabled="loading || mutationBusy" @click="searchContextSources(false)">Cargar más</button>
            <div class="tool-context-picker__footer">
              <div class="tool-context-picker__readiness" :class="{ 'is-ready': selectedContextSourcesReady }" role="status" aria-live="polite">
                <span aria-hidden="true">{{ selectedContextSourcesReady ? '✓' : 'i' }}</span>
                <div>
                  <strong v-if="structuredRequestItems.length">{{ coveredRequestItemsCount }} de {{ structuredRequestItems.length }} solicitudes cubiertas</strong>
                  <strong v-else>{{ contextSelectionStatus }}</strong>
                  <small>{{ contextReadinessMessage }}</small>
                </div>
              </div>
              <div class="tool-context-picker__actions">
                <button type="button" class="tool-context-picker__action tool-context-picker__action--primary" :disabled="mutationBusy || uploadingContextDocument || !selectedContextSourcesReady" @click="resolveContextRequest('selected')">{{ mutationBusy ? 'Continuando…' : contextContinueLabel }}</button>
                <button type="button" class="tool-context-picker__action tool-context-picker__action--secondary" :disabled="mutationBusy || uploadingContextDocument" @click="resolveContextRequest('skip')">Continuar sin documentos</button>
                <button type="button" class="tool-context-picker__action tool-context-picker__action--quiet" :disabled="mutationBusy || uploadingContextDocument" @click="resolveContextRequest('cancel')">Cancelar solicitud</button>
              </div>
            </div>
          </section>
          <template v-else-if="projection">
            <div v-if="resultRef && resultRef.type === 'document'" class="tool-workspace__document">
              <div ref="responseAudioActions" class="response-audio-slot"></div>
              <div class="tool-workspace__meta">
                <span>Versión {{ projection.selected_version }}</span>
                <span v-if="projection.change_summary">{{ projection.change_summary }}</span>
              </div>
              <nav v-if="versions.length" class="tool-workspace__versions" aria-label="Historial de versiones">
                <button
                  v-for="version in versions"
                  :key="version.version_number"
                  type="button"
                  :aria-current="Number(projection.selected_version) === Number(version.version_number) ? 'true' : null"
                  :aria-label="version.available === false ? ('Versión ' + version.version_number + ' no disponible') : ('Abrir versión ' + version.version_number)"
                  :title="version.available === false ? 'Procedencia no disponible' : (version.change_summary || ('Versión ' + version.version_number))"
                  :disabled="version.available === false"
                  @click="openArtifactVersion(version.version_number)"
                >v{{ version.version_number }}</button>
              </nav>
              <div
                v-if="artifactProvenanceUnavailable"
                class="tool-workspace__state"
                data-testid="workspace-artifact-provenance-unavailable"
                role="status"
              >
                <strong>No pudimos abrir esta versión.</strong>
                <span>{{ projection.message || 'No pudimos comprobar el acceso a una fuente del documento.' }}</span>
                <span>El documento sigue guardado. Puedes pedir al asistente que lo recupere.</span>
                <button type="button" data-testid="workspace-artifact-provenance-retry" :disabled="loading" @click="retryToolResultLoad">Reintentar</button>
              </div>
              <article v-else class="tool-workspace__markdown markdown-body" v-html="renderedArtifact"></article>
              <p v-for="(warning, index) in (projection.warnings || [])" :key="'document-warning-' + index" role="status">{{ warning }}</p>
              <div v-if="!artifactProvenanceUnavailable" class="tool-workspace__footer-actions">
                <button type="button" class="tool-workspace__footer-action--primary" @click="copyArtifact">Copiar documento</button>
                <button type="button" class="tool-workspace__footer-action--primary" @click="downloadExport('docx')">Descargar Word</button>
                <button type="button" class="tool-workspace__footer-action--primary" @click="downloadExport('pdf')">Descargar PDF</button>
                <button
                  v-if="Number(projection.selected_version) !== Number(projection.current_version)"
                  type="button"
                  :disabled="mutationBusy"
                  @click="restoreSelectedVersion"
                >Restaurar como nueva versión</button>
              </div>
            </div>
            <div
              v-else-if="resultRef && resultRef.type === 'review'"
              class="tool-workspace__review"
              :data-review-profile="reviewIsInstitutional ? 'institutional' : (reviewIsEditable ? 'editable' : 'read-only')"
              data-tour="document-review-workspace"
            >
              <section class="tool-review-overview" data-tour="document-review-overview" aria-labelledby="review-overview-heading">
                <div class="tool-review-overview__lead">
                  <span class="tool-review-overview__mark" aria-hidden="true"><i class="anticon anticon-audit"></i></span>
                  <div>
                    <span class="tool-review-overview__eyebrow">Resultado de la revisión</span>
                    <h3 id="review-overview-heading">
                      {{ findings.length ? (findings.length + ' hallazgo' + (findings.length === 1 ? '' : 's') + (reviewIsInstitutional ? ' para evaluar' : ' para revisar')) : 'Sin hallazgos materiales' }}
                    </h3>
                    <p v-if="reviewAnalysisSummary" class="tool-workspace__analysis-summary">{{ reviewAnalysisSummary }}</p>
                  </div>
                </div>
                <dl class="tool-review-stats" aria-label="Resumen de hallazgos y fuentes">
                  <div v-for="stat in reviewStats" :key="stat.label" :class="'tool-review-stats__item--' + stat.tone">
                    <dt>{{ stat.label }}</dt>
                    <dd>{{ stat.value }}</dd>
                  </div>
                </dl>
                <dl v-if="reviewOverviewProvenanceItems.length" class="tool-review-provenance" data-testid="review-provenance">
                  <div v-for="item in reviewOverviewProvenanceItems" :key="item.label">
                    <dt>{{ item.label }}</dt>
                    <dd>{{ item.value }}</dd>
                  </div>
                </dl>
                <p v-if="reviewWarningText" class="tool-workspace__notice tool-workspace__notice--warning" role="status">
                  <span class="anticon anticon-warning" aria-hidden="true"></span>
                  <span><strong>{{ reviewIsPartial ? 'Revisión incompleta.' : 'Advertencia.' }}</strong> {{ reviewWarningText }}</span>
                </p>
                <p
                  v-if="reviewInstitutionalNotice"
                  class="tool-workspace__notice tool-workspace__notice--info"
                  data-testid="review-institutional-caution"
                  role="status"
                >
                  <span class="anticon anticon-safety-certificate" aria-hidden="true"></span>
                  <span><strong>Revisión cautelosa de contenido institucional.</strong> {{ reviewInstitutionalNotice }}</span>
                </p>
              </section>

              <header class="tool-review-section-heading tool-review-section-heading--findings">
                <div>
                  <span>Tu siguiente paso</span>
                  <h3 id="review-findings-heading">{{ reviewFindingsHeading }}</h3>
                </div>
              </header>
              <div v-if="!findings.length" class="tool-review-empty">
                <span class="anticon anticon-check-circle" aria-hidden="true"></span>
                <div>
                  <strong>No se identificaron hallazgos materiales</strong>
                  <p>{{ reviewIsInstitutional ? 'No se identificaron posibles incidencias que debas evaluar.' : 'No hace falta que tomes ninguna decisión en esta revisión.' }}</p>
                </div>
              </div>
              <section
                v-else-if="reviewPendingFindings.length"
                class="tool-review-pending-guide"
                data-tour="document-review-pending-guide"
                aria-labelledby="review-pending-guide-heading"
              >
                <div class="tool-review-pending-guide__heading">
                  <span class="anticon anticon-clock-circle" aria-hidden="true"></span>
                  <div>
                    <span>Solo por decidir</span>
                    <h4 id="review-pending-guide-heading">Recomendaciones pendientes</h4>
                  </div>
                </div>
                <ol class="tool-review-pending-titles" aria-label="Títulos de las recomendaciones pendientes">
                  <li
                    v-for="finding in visibleOpenReviewFindings"
                    :key="'pending-title-' + finding.finding_id"
                    :data-pending-title-id="finding.finding_id"
                  >
                    <button
                      type="button"
                      :aria-label="'Ir al hallazgo ' + reviewFindingOrdinal(finding) + ': ' + finding.title"
                      @click="scrollToReviewFinding(finding)"
                    >
                      <span aria-hidden="true">{{ reviewFindingOrdinal(finding) }}</span>
                      <strong>{{ finding.title }}</strong>
                    </button>
                  </li>
                </ol>
                <div v-if="reviewCanShowBulkActions" class="tool-review-bulk-actions" aria-label="Decidir las recomendaciones restantes">
                  <p>También puedes decidir las restantes de una vez.</p>
                  <div>
                    <button
                      type="button"
                      class="tool-review-bulk-actions__approve"
                      :disabled="mutationBusy"
                      @click="setRemainingFindingsDisposition('accepted')"
                    >Aprobar las restantes</button>
                    <button
                      type="button"
                      class="tool-review-bulk-actions__decline"
                      :disabled="mutationBusy"
                      @click="setRemainingFindingsDisposition('dismissed')"
                    >No aplicar las restantes</button>
                  </div>
                </div>
              </section>
              <div
                v-if="findings.length"
                class="tool-review-filter"
                role="group"
                aria-label="Filtrar hallazgos de la revisión"
              >
                <button
                  ref="reviewPendingFilterButton"
                  type="button"
                  :aria-pressed="reviewIsPendingFilter ? 'true' : 'false'"
                  @click="setReviewFilter('pending')"
                >Por decidir <strong>{{ reviewPendingFindingCount }}</strong></button>
                <button
                  type="button"
                  :aria-pressed="!reviewIsPendingFilter ? 'true' : 'false'"
                  @click="setReviewFilter('all')"
                >Todos <strong>{{ findings.length }}</strong></button>
              </div>
              <div
                v-if="!reviewPendingFindings.length && reviewIsPendingFilter && findings.length"
                class="tool-review-empty tool-review-empty--filtered"
                role="status"
              >
                <span class="anticon anticon-check-circle" aria-hidden="true"></span>
                <div>
                  <strong>No quedan recomendaciones por decidir</strong>
                  <p>Puedes volver a todos los hallazgos para consultar tus decisiones.</p>
                  <button type="button" @click="setReviewFilter('all')">Ver todos los hallazgos</button>
                </div>
              </div>
              <article
                v-for="finding in visibleReviewFindings"
                :key="finding.finding_id"
                :data-finding-id="finding.finding_id"
                tabindex="-1"
                :data-tour="finding === reviewTourFinding ? 'document-review-finding' : null"
                :class="[
                  'tool-finding',
                  'tool-finding--' + String(finding.disposition || 'open').toLowerCase(),
                  pendingReviewDecisionReceipt(finding) ? 'tool-finding--decision-receipt' : '',
                ]"
              >
                <div
                  v-if="pendingReviewDecisionReceipt(finding)"
                  class="tool-finding__decision-receipt"
                  :data-review-action="pendingReviewDecisionReceipt(finding).disposition"
                  role="status"
                  aria-live="polite"
                  aria-atomic="true"
                >
                  <span class="tool-finding__decision-receipt-mark" aria-hidden="true">
                    <i class="anticon anticon-check"></i>
                  </span>
                  <div class="tool-finding__decision-receipt-copy">
                    <span>Decisión guardada</span>
                    <strong>{{ reviewDecisionReceiptLabel(finding) }}</strong>
                    <p><b>“{{ finding.title }}”</b> salió de <em>Por decidir</em>. Esta confirmación conserva su lugar para que no lo confundas con otro hallazgo.</p>
                  </div>
                  <button
                    type="button"
                    class="tool-finding__decision-receipt-dismiss"
                    :aria-label="'Ocultar confirmación de ' + finding.title"
                    @click="dismissPendingReviewDecisionReceipt(finding.finding_id)"
                  >Ocultar</button>
                  <span class="tool-finding__decision-receipt-stamp" aria-hidden="true">Guardado</span>
                </div>
                <header v-if="!pendingReviewDecisionReceipt(finding)" class="tool-finding__header">
                  <div>
                    <span class="tool-finding__index">Hallazgo {{ reviewFindingOrdinal(finding) }}</span>
                    <div class="tool-finding__labels">
                      <span :class="['tool-finding__badge', severityClass(finding.severity)]">{{ reviewSeverityPrefix }} {{ severityLabel(finding.severity).toLowerCase() }}</span>
                      <span :class="['tool-finding__badge', dispositionClass(finding.disposition)]">{{ dispositionLabel(finding.disposition) }}</span>
                    </div>
                  </div>
                  <span :class="['tool-finding__classification', classificationClass(finding.classification)]">{{ classificationLabel(finding.classification) }}</span>
                </header>
                <p
                  v-if="!pendingReviewDecisionReceipt(finding) && findingCompletion(finding)"
                  class="tool-finding__completion"
                  data-testid="review-finding-completion"
                  :data-completion-kind="findingCompletion(finding).kind"
                  role="status"
                  aria-live="polite"
                  aria-atomic="true"
                >
                  <span class="tool-finding__completion-mark" aria-hidden="true"><i :class="findingCompletion(finding).kind.startsWith('partially-applied') ? 'anticon anticon-exclamation-circle' : 'anticon anticon-check'"></i></span>
                  <strong>{{ findingCompletion(finding).label }}</strong>
                </p>
                <div v-if="!pendingReviewDecisionReceipt(finding)" class="tool-finding__body">
                  <h3>{{ finding.title }}</h3>
                  <p>{{ finding.explanation }}</p>
                </div>
                <div v-if="!pendingReviewDecisionReceipt(finding) && finding.evidence && finding.evidence.recommendation" class="tool-finding__recommendation">
                  <span class="anticon anticon-bulb" aria-hidden="true"></span>
                  <p><strong>{{ reviewIsInstitutional ? 'Siguiente verificación' : 'Recomendación' }}</strong>{{ finding.evidence.recommendation }}</p>
                </div>
                <details
                  v-if="!pendingReviewDecisionReceipt(finding) && findingDocumentEvidence(finding).length"
                  class="tool-finding__evidence-disclosure tool-finding__document-evidence"
                  :data-tour="finding === reviewTourFinding ? 'document-review-evidence' : null"
                >
                  <summary>
                    <span>Evidencia documental</span>
                    <strong>{{ findingDocumentEvidence(finding).length }}</strong>
                  </summary>
                  <article
                    v-for="(evidence, evidenceIndex) in findingDocumentEvidence(finding)"
                    :key="evidenceOccurrenceKey('document', finding.finding_id, evidenceIndex, evidence) || ('document:' + evidenceIndex)"
                    class="tool-review-source-card"
                  >
                    <div class="tool-review-source-card__heading">
                      <div>
                        <h4>{{ evidenceRoleLabel(evidence.role) }}</h4>
                        <p v-if="locatorText(evidence.locator)" class="tool-review-source-card__locator">
                          <span>{{ locatorText(evidence.locator) }}</span>
                        </p>
                      </div>
                      <button
                        v-if="evidenceAnchorId(evidence)"
                        type="button"
                        class="tool-finding__evidence-button"
                        :data-source-occurrence-key="evidenceOccurrenceKey('document', finding.finding_id, evidenceIndex, evidence)"
                        :aria-expanded="isAnchorOpen(evidenceOccurrenceKey('document', finding.finding_id, evidenceIndex, evidence)) ? 'true' : 'false'"
                        :disabled="isAnchorLoading(evidenceOccurrenceKey('document', finding.finding_id, evidenceIndex, evidence))"
                        @click="toggleExactSource(evidence, $event, evidenceOccurrenceKey('document', finding.finding_id, evidenceIndex, evidence))"
                      >{{ isAnchorLoading(evidenceOccurrenceKey('document', finding.finding_id, evidenceIndex, evidence)) ? 'Abriendo…' : (isAnchorOpen(evidenceOccurrenceKey('document', finding.finding_id, evidenceIndex, evidence)) ? 'Ocultar contexto' : 'Ver contexto completo') }}</button>
                    </div>
                    <blockquote v-if="evidence.quote" v-html="renderMarkdown(evidence.quote)"></blockquote>
                    <div
                      v-if="isAnchorOpen(evidenceOccurrenceKey('document', finding.finding_id, evidenceIndex, evidence))"
                      class="tool-review-exact-source"
                      :class="{ 'tool-review-exact-source--located': exactSourceHasHighlight(exactAnchorQuote(evidenceAnchorId(evidence)), evidence.quote) }"
                      :data-source-anchor-id="evidenceAnchorId(evidence)"
                      :data-source-occurrence-key="evidenceOccurrenceKey('document', finding.finding_id, evidenceIndex, evidence)"
                      role="region"
                      aria-label="Evidencia documental exacta"
                    >
                      <span
                        v-if="exactSourceHasHighlight(exactAnchorQuote(evidenceAnchorId(evidence)), evidence.quote)"
                        class="tool-review-exact-source__label"
                      ><span aria-hidden="true"></span>Pasaje señalado en la fuente</span>
                      <span v-else class="tool-review-exact-source__label tool-review-exact-source__label--context">Contexto original</span>
                      <p v-if="exactAnchorLocator(evidenceAnchorId(evidence))" class="tool-review-source-card__locator">
                        <span>{{ exactAnchorLocator(evidenceAnchorId(evidence)) }}</span>
                      </p>
                      <blockquote v-if="exactAnchorQuote(evidenceAnchorId(evidence))" class="tool-finding__exact-evidence" v-html="renderHighlightedSource(exactAnchorQuote(evidenceAnchorId(evidence)), evidence.quote)"></blockquote>
                    </div>
                    <p v-if="anchorLoadError(evidenceOccurrenceKey('document', finding.finding_id, evidenceIndex, evidence))" class="tool-review-source-card__error" role="alert">{{ anchorLoadError(evidenceOccurrenceKey('document', finding.finding_id, evidenceIndex, evidence)) }}</p>
                  </article>
                </details>
                <details
                  v-if="!pendingReviewDecisionReceipt(finding) && finding.evidence && Array.isArray(finding.evidence.legal_bases) && finding.evidence.legal_bases.length"
                  class="tool-finding__evidence-disclosure tool-finding__legal-bases"
                  :data-tour="finding === reviewTourFinding ? 'document-review-legal-bases' : null"
                >
                  <summary>
                    <span>Fundamento jurídico</span>
                    <strong>{{ finding.evidence.legal_bases.length }}</strong>
                  </summary>
                  <article
                    v-for="(basis, basisIndex) in finding.evidence.legal_bases"
                    :key="evidenceOccurrenceKey('legal', finding.finding_id, basisIndex, basis) || ('legal:' + basisIndex)"
                    class="tool-review-source-card tool-review-source-card--authority"
                  >
                    <div class="tool-review-source-card__heading">
                      <div>
                        <h4>{{ basis.source_title || 'Fuente jurídica' }}</h4>
                        <p v-if="legalSourceMetadata(basis)" class="tool-review-source-card__locator">
                          <span>{{ legalSourceMetadata(basis) }}</span>
                        </p>
                      </div>
                      <button
                        v-if="evidenceAnchorId(basis)"
                        type="button"
                        class="tool-finding__evidence-button"
                        :data-source-occurrence-key="evidenceOccurrenceKey('legal', finding.finding_id, basisIndex, basis)"
                        :aria-expanded="isAnchorOpen(evidenceOccurrenceKey('legal', finding.finding_id, basisIndex, basis)) ? 'true' : 'false'"
                        :disabled="isAnchorLoading(evidenceOccurrenceKey('legal', finding.finding_id, basisIndex, basis))"
                        @click="toggleExactSource(basis, $event, evidenceOccurrenceKey('legal', finding.finding_id, basisIndex, basis))"
                      >{{ isAnchorLoading(evidenceOccurrenceKey('legal', finding.finding_id, basisIndex, basis)) ? 'Abriendo…' : (isAnchorOpen(evidenceOccurrenceKey('legal', finding.finding_id, basisIndex, basis)) ? 'Ocultar contexto' : 'Ver contexto en la fuente') }}</button>
                    </div>
                    <blockquote v-if="basis.quote" v-html="renderMarkdown(basis.quote)"></blockquote>
                    <p v-if="basis.relevance" class="tool-review-source-card__relevance">
                      <strong>Por qué aplica</strong>{{ basis.relevance }}
                    </p>
                    <div
                      v-if="isAnchorOpen(evidenceOccurrenceKey('legal', finding.finding_id, basisIndex, basis))"
                      class="tool-review-exact-source"
                      :class="{ 'tool-review-exact-source--located': exactSourceHasHighlight(exactAnchorQuote(evidenceAnchorId(basis)), basis.quote) }"
                      :data-source-anchor-id="evidenceAnchorId(basis)"
                      :data-source-occurrence-key="evidenceOccurrenceKey('legal', finding.finding_id, basisIndex, basis)"
                      role="region"
                      aria-label="Fundamento jurídico exacto"
                    >
                      <span
                        v-if="exactSourceHasHighlight(exactAnchorQuote(evidenceAnchorId(basis)), basis.quote)"
                        class="tool-review-exact-source__label"
                      ><span aria-hidden="true"></span>Pasaje señalado en la fuente</span>
                      <span v-else class="tool-review-exact-source__label tool-review-exact-source__label--context">Contexto original</span>
                      <p v-if="exactAnchorLocator(evidenceAnchorId(basis))" class="tool-review-source-card__locator">
                        <span>{{ exactAnchorLocator(evidenceAnchorId(basis)) }}</span>
                      </p>
                      <blockquote v-if="exactAnchorQuote(evidenceAnchorId(basis))" v-html="renderHighlightedSource(exactAnchorQuote(evidenceAnchorId(basis)), basis.quote)"></blockquote>
                    </div>
                    <p v-if="anchorLoadError(evidenceOccurrenceKey('legal', finding.finding_id, basisIndex, basis))" class="tool-review-source-card__error" role="alert">{{ anchorLoadError(evidenceOccurrenceKey('legal', finding.finding_id, basisIndex, basis)) }}</p>
                  </article>
                </details>
                <details
                  v-if="!pendingReviewDecisionReceipt(finding) && finding.evidence && finding.evidence.counteranalysis"
                  class="tool-finding__counteranalysis"
                  :data-tour="finding === reviewTourFinding ? 'document-review-interpretation' : null"
                >
                  <summary>Interpretación alternativa considerada</summary>
                  <p>{{ finding.evidence.counteranalysis }}</p>
                </details>
                <div v-if="!pendingReviewDecisionReceipt(finding)" class="tool-finding__disposition-panel">
                  <div class="tool-finding__disposition-summary">
                    <div>
                      <span class="tool-finding__decision-kicker">Tu decisión</span>
                      <strong>¿Cómo quieres tratar este hallazgo?</strong>
                    </div>
                    <span class="tool-finding__current-state">Estado actual: {{ dispositionLabel(finding.disposition) }}</span>
                    <span v-if="dispositionAuditText(finding)">{{ dispositionAuditText(finding) }}</span>
                    <span v-if="finding.disposition_reason">Nota guardada: {{ finding.disposition_reason }}</span>
                    <span v-if="activeFindingMutationId === finding.finding_id" class="tool-finding__save-status" role="status">Guardando decisión…</span>
                    <span v-else-if="savedFindingId === finding.finding_id" class="tool-finding__save-status tool-finding__save-status--saved" role="status">Decisión guardada</span>
                  </div>
                  <label
                    class="tool-finding__reason"
                    :data-tour="finding === reviewTourFinding ? 'document-review-note' : null"
                  >
                    <span>Nota para la IA <small>(opcional)</small></span>
                    <textarea
                      :value="dispositionReasonDraft(finding)"
                      rows="2"
                      maxlength="500"
                      :disabled="mutationBusy || reviewDecisionConfirmationPending"
                      :placeholder="reviewNotePlaceholder"
                      @input="setDispositionReasonDraft(finding, $event.target.value)"
                    ></textarea>
                  </label>
                  <div
                    class="tool-finding__actions"
                    :data-tour="finding === reviewTourFinding ? 'document-review-actions' : null"
                  >
                    <button
                      v-for="action in reviewFindingActions"
                      :key="action.disposition"
                      type="button"
                      :class="'tool-finding__action--' + action.disposition"
                      :data-review-action="action.disposition"
                      :aria-pressed="finding.disposition === action.disposition ? 'true' : 'false'"
                      :disabled="mutationBusy || reviewDecisionConfirmationPending || finding.disposition === action.disposition"
                      @click="setFindingDisposition(finding, action.disposition, $event)"
                    ><span class="tool-finding__action-mark" aria-hidden="true">{{ action.mark }}</span><span><strong>{{ action.label }}</strong><small>{{ reviewFindingActionDetail(finding, action) }}</small></span></button>
                    <button
                      v-if="finding.disposition !== 'open' && reviewAllowedDispositions.includes('open')"
                      type="button"
                      class="tool-finding__action--reopen"
                      data-review-action="open"
                      :disabled="mutationBusy || reviewDecisionConfirmationPending"
                      @click="setFindingDisposition(finding, 'open', $event)"
                    >{{ reopenFindingLabel(finding) }}</button>
                  </div>
                </div>
              </article>
              <details
                v-if="reviewLegalFramework.length"
                class="tool-workspace__legal-framework tool-review-disclosure"
              >
                <summary>
                  <span>Marco jurídico consultado</span>
                  <strong>{{ reviewLegalFramework.length }}</strong>
                </summary>
                <p class="tool-workspace__meta">Autoridades exactas utilizadas para contextualizar la revisión. Los fundamentos directamente vinculados también aparecen dentro de cada hallazgo.</p>
                <article
                  v-for="(basis, basisIndex) in reviewLegalFramework"
                  :key="evidenceOccurrenceKey('framework', 'review', basisIndex, basis) || ('framework:' + basisIndex)"
                  class="tool-review-source-card tool-review-source-card--authority"
                >
                  <div class="tool-review-source-card__heading">
                    <div>
                      <h4>{{ basis.source_title || 'Fuente jurídica' }}</h4>
                      <p v-if="legalSourceMetadata(basis)" class="tool-review-source-card__locator">
                        <span>{{ legalSourceMetadata(basis) }}</span>
                      </p>
                    </div>
                    <button
                      v-if="evidenceAnchorId(basis)"
                      type="button"
                      class="tool-finding__evidence-button"
                      :data-source-occurrence-key="evidenceOccurrenceKey('framework', 'review', basisIndex, basis)"
                      :aria-expanded="isAnchorOpen(evidenceOccurrenceKey('framework', 'review', basisIndex, basis)) ? 'true' : 'false'"
                      :disabled="isAnchorLoading(evidenceOccurrenceKey('framework', 'review', basisIndex, basis))"
                      @click="toggleExactSource(basis, $event, evidenceOccurrenceKey('framework', 'review', basisIndex, basis))"
                    >{{ isAnchorLoading(evidenceOccurrenceKey('framework', 'review', basisIndex, basis)) ? 'Abriendo…' : (isAnchorOpen(evidenceOccurrenceKey('framework', 'review', basisIndex, basis)) ? 'Ocultar contexto' : 'Ver contexto en la fuente') }}</button>
                  </div>
                  <blockquote v-if="basis.quote" v-html="renderMarkdown(basis.quote)"></blockquote>
                  <p v-if="basis.relevance" class="tool-review-source-card__relevance">
                    <strong>Por qué se consultó</strong>{{ basis.relevance }}
                  </p>
                  <div
                    v-if="isAnchorOpen(evidenceOccurrenceKey('framework', 'review', basisIndex, basis))"
                    class="tool-review-exact-source"
                    :class="{ 'tool-review-exact-source--located': exactSourceHasHighlight(exactAnchorQuote(evidenceAnchorId(basis)), basis.quote) }"
                    :data-source-anchor-id="evidenceAnchorId(basis)"
                    :data-source-occurrence-key="evidenceOccurrenceKey('framework', 'review', basisIndex, basis)"
                    role="region"
                    aria-label="Autoridad jurídica exacta"
                  >
                    <span
                      v-if="exactSourceHasHighlight(exactAnchorQuote(evidenceAnchorId(basis)), basis.quote)"
                      class="tool-review-exact-source__label"
                    ><span aria-hidden="true"></span>Pasaje señalado en la fuente</span>
                    <span v-else class="tool-review-exact-source__label tool-review-exact-source__label--context">Contexto original</span>
                    <p v-if="exactAnchorLocator(evidenceAnchorId(basis))" class="tool-review-source-card__locator">
                      <span>{{ exactAnchorLocator(evidenceAnchorId(basis)) }}</span>
                    </p>
                    <blockquote v-if="exactAnchorQuote(evidenceAnchorId(basis))" v-html="renderHighlightedSource(exactAnchorQuote(evidenceAnchorId(basis)), basis.quote)"></blockquote>
                  </div>
                  <p v-if="anchorLoadError(evidenceOccurrenceKey('framework', 'review', basisIndex, basis))" class="tool-review-source-card__error" role="alert">{{ anchorLoadError(evidenceOccurrenceKey('framework', 'review', basisIndex, basis)) }}</p>
                </article>
              </details>
              <details
                v-if="reviewCheckedCategoryLabels.length"
                class="tool-review-technical"
                data-tour="document-review-aspects"
              >
                <summary>
                  <span><i class="anticon anticon-profile" aria-hidden="true"></i> Aspectos revisados</span>
                  <small>{{ reviewCheckedCategoryLabels.length === 1 ? '1 aspecto' : (reviewCheckedCategoryLabels.length + ' aspectos') }}</small>
                </summary>
                <p class="tool-workspace__meta">
                  {{ reviewCheckedCategoryLabels.join(', ') }}.
                </p>
              </details>
              <p
                class="tool-workspace__notice tool-workspace__notice--info"
                data-tour="document-review-guidance"
              >
                <span class="anticon anticon-info-circle" aria-hidden="true"></span>
                <span v-if="reviewIsInstitutional">LEXIUS señaló posibles incidencias a partir de los pasajes mostrados. No constituyen una afirmación de error en la fuente institucional: compruébalas en la publicación oficial vigente y su contexto completo. Esta revisión asistida no sustituye la valoración profesional del caso concreto.</span>
                <span v-else>LEXIUS contrastó el documento con los pasajes y fuentes mostrados aquí. Esta revisión asistida no sustituye la valoración profesional del caso concreto.</span>
              </p>
              <footer v-if="reviewIsEditable" class="tool-review-apply-footer">
                <aside
                  v-if="reviewApplyConfirmation"
                  class="tool-review-apply-confirmation"
                  data-testid="review-apply-confirmation"
                  role="dialog"
                  aria-modal="false"
                  aria-labelledby="review-apply-confirmation-title"
                  aria-describedby="review-apply-confirmation-copy"
                  @click.stop
                >
                  <header class="tool-review-apply-confirmation__header">
                    <div>
                      <span>Antes de solicitar la edición</span>
                      <h3 id="review-apply-confirmation-title">Confirma qué cambiará</h3>
                    </div>
                    <button
                      type="button"
                      class="tool-review-apply-confirmation__close"
                      aria-label="Cerrar confirmación"
                      @click="dismissReviewApplyConfirmation"
                    ><i class="anticon anticon-close" aria-hidden="true"></i></button>
                  </header>
                  <p id="review-apply-confirmation-copy" class="tool-review-apply-confirmation__copy">
                    Se solicitará una nueva versión<span v-if="reviewApplyConfirmation.documentTitle"> de <strong>{{ reviewApplyConfirmation.documentTitle }}</strong></span> con las recomendaciones aprobadas.
                  </p>
                  <div
                    class="tool-review-apply-confirmation__decisions"
                    :class="{ 'tool-review-apply-confirmation__decisions--approved-only': !reviewApplyConfirmation.pending.length }"
                  >
                    <section class="tool-review-apply-confirmation__group tool-review-apply-confirmation__group--approved">
                      <div class="tool-review-apply-confirmation__group-heading">
                        <span class="anticon anticon-check-circle" aria-hidden="true"></span>
                        <div>
                          <span>{{ reviewApplyConfirmation.approved.length }} {{ reviewApplyConfirmation.approved.length === 1 ? 'aprobada' : 'aprobadas' }}</span>
                          <h4>Se solicitarán estos cambios</h4>
                        </div>
                      </div>
                      <ol aria-label="Recomendaciones que se aplicarán">
                        <li v-for="finding in reviewApplyConfirmation.approved" :key="'apply-approved-' + finding.findingId">
                          <span aria-hidden="true">{{ finding.ordinal }}</span>
                          <div>
                            <strong>{{ finding.title }}</strong>
                            <small v-if="finding.recommendation">{{ finding.recommendation }}</small>
                          </div>
                        </li>
                      </ol>
                    </section>
                    <section
                      v-if="reviewApplyConfirmation.pending.length"
                      class="tool-review-apply-confirmation__group tool-review-apply-confirmation__group--pending"
                    >
                      <div class="tool-review-apply-confirmation__group-heading">
                        <span class="anticon anticon-clock-circle" aria-hidden="true"></span>
                        <div>
                          <span>{{ reviewApplyConfirmation.pending.length }} por decidir</span>
                          <h4>Quedarán fuera por ahora</h4>
                        </div>
                      </div>
                      <p>No se considerarán en esta edición hasta que las apruebes o descartes.</p>
                      <ol aria-label="Hallazgos que no se considerarán">
                        <li v-for="finding in reviewApplyConfirmation.pending" :key="'apply-pending-' + finding.findingId">
                          <span aria-hidden="true">{{ finding.ordinal }}</span>
                          <strong>{{ finding.title }}</strong>
                        </li>
                      </ol>
                    </section>
                  </div>
                  <div class="tool-review-apply-confirmation__actions">
                    <button
                      v-if="reviewApplyConfirmation.pending.length"
                      type="button"
                      class="tool-review-apply-confirmation__review"
                      @click="returnToPendingReviewFindings"
                    >Revisar {{ reviewApplyConfirmation.pending.length }} {{ reviewApplyConfirmation.pending.length === 1 ? 'pendiente' : 'pendientes' }}</button>
                    <button type="button" class="tool-review-apply-confirmation__cancel" @click="dismissReviewApplyConfirmation">Cancelar</button>
                    <button
                      ref="reviewApplyConfirmButton"
                      type="button"
                      class="tool-review-apply-confirmation__confirm"
                      @click="confirmApplyApprovedReviewChanges"
                    >Solicitar edición</button>
                  </div>
                </aside>
                <div role="status" aria-live="polite">
                  <strong>{{ reviewApprovedPendingCount }}</strong>
                  <span>{{ reviewApprovedPendingCount === 1 ? 'recomendación pendiente de aplicar' : 'recomendaciones pendientes de aplicar' }}</span>
                </div>
                <button
                  ref="reviewApplyTrigger"
                  type="button"
                  :disabled="mutationBusy || reviewDecisionConfirmationPending || !reviewCanApplyApproved"
                  :aria-expanded="reviewApplyConfirmation ? 'true' : 'false'"
                  aria-haspopup="dialog"
                  @click="openReviewApplyConfirmation()"
                >Aplicar {{ reviewApprovedPendingCount }} {{ reviewApprovedPendingCount === 1 ? 'aprobada' : 'aprobadas' }}</button>
              </footer>
            </div>
            <div v-else-if="isSourceWorkspace" class="tool-workspace__anchor">
              <section class="tool-source-overview" aria-labelledby="tool-source-overview-title">
                <div class="tool-source-overview__heading">
                  <span class="tool-source-overview__mark" aria-hidden="true"><i class="anticon anticon-safety-certificate"></i></span>
                  <div>
                    <span>Lectura verificada</span>
                    <h3 id="tool-source-overview-title">Qué consultó LEXIUS</h3>
                    <p>Cada pasaje conserva su fuente, ubicación y tipo de coincidencia para que puedas comprobarlo.</p>
                  </div>
                </div>
                <dl class="tool-source-overview__stats" aria-label="Disponibilidad de las fuentes">
                  <div v-for="stat in sourceOverviewStats" :key="stat.label" :class="'is-' + stat.tone">
                    <dd>{{ stat.value }}</dd>
                    <dt>{{ stat.label }}</dt>
                  </div>
                </dl>
                <p v-if="sourceUnavailableCount > 0" class="tool-source-overview__notice" role="status">
                  <i class="anticon anticon-lock" aria-hidden="true"></i>
                  <span>
                    {{ sourceUnavailableCount }} {{ sourceUnavailableCount === 1 ? 'fuente no está disponible' : 'fuentes no están disponibles' }}.
                    <template v-if="sourceUnavailablePassageCount"> {{ sourceUnavailablePassageCount }} {{ sourceUnavailablePassageCount === 1 ? 'pasaje se ocultó' : 'pasajes se ocultaron' }} para proteger el acceso.</template>
                    <template v-else> Se muestran solo los pasajes que todavía puedes consultar.</template>
                  </span>
                </p>
                <ul v-if="sourceWarnings.length" class="tool-source-overview__warnings">
                  <li v-for="(warning, warningIndex) in sourceWarnings" :key="warningIndex">{{ warning }}</li>
                </ul>
              </section>
              <div v-if="!anchorPassages.length" class="tool-source-empty" role="status">
                <span aria-hidden="true"><i class="anticon anticon-file-search"></i></span>
                <div>
                  <h3>No hay pasajes disponibles</h3>
                  <p>{{ isInlineSourceRead ? 'La lectura no devolvió pasajes verificables para este resumen.' : 'Las fuentes pudieron cambiar o dejar de estar autorizadas. Vuelve al chat para hacer una nueva consulta.' }}</p>
                </div>
              </div>
              <ol v-else class="tool-source-ledger" aria-label="Pasajes exactos consultados">
                <li v-for="(passage, passageIndex) in anchorPassages" :key="passage.anchor_id || passage.quote">
                  <article class="tool-workspace__passage">
                    <header class="tool-workspace__passage-header">
                      <div>
                        <span class="tool-workspace__passage-folio">Pasaje {{ passageIndex + 1 }}</span>
                        <h3>{{ passage.source_title || 'Fuente autorizada' }}</h3>
                      </div>
                      <button
                        type="button"
                        class="tool-workspace__passage-copy"
                        :class="{ 'is-copied': copiedPassageKey === sourcePassageKey(passage, passageIndex) }"
                        :aria-label="copiedPassageKey === sourcePassageKey(passage, passageIndex) ? 'Pasaje copiado' : ('Copiar pasaje ' + (passageIndex + 1))"
                        @click="copySourcePassage(passage, passageIndex)"
                      ><i :class="copiedPassageKey === sourcePassageKey(passage, passageIndex) ? 'anticon anticon-check' : 'anticon anticon-copy'" aria-hidden="true"></i><span>{{ copiedPassageKey === sourcePassageKey(passage, passageIndex) ? 'Copiado' : 'Copiar' }}</span></button>
                    </header>
                    <ul v-if="passage.revision || locatorText(passage.locator) || sourceMatchLabel(passage.match_kind) || passage.content_truncated" class="tool-workspace__passage-meta" aria-label="Procedencia del pasaje">
                      <li v-if="locatorText(passage.locator)" class="is-locator"><i class="anticon anticon-environment" aria-hidden="true"></i>{{ locatorText(passage.locator) }}</li>
                      <li v-if="sourceMatchLabel(passage.match_kind)" :class="'is-' + sourceMatchTone(passage.match_kind)"><i class="anticon anticon-search" aria-hidden="true"></i>{{ sourceMatchLabel(passage.match_kind) }}</li>
                      <li v-if="passage.revision" class="is-version"><i class="anticon anticon-check-circle" aria-hidden="true"></i>Versión de fuente verificada</li>
                      <li v-if="passage.content_truncated" class="is-attention"><i class="anticon anticon-warning" aria-hidden="true"></i>Extracción parcial</li>
                    </ul>
                    <p v-if="passage.content_truncated" class="tool-workspace__passage-warning">
                      La extracción se truncó; el contenido disponible puede estar incompleto.
                    </p>
                    <details class="tool-workspace__quote" :open="passageIndex === 0">
                      <summary><span><i class="anticon anticon-read" aria-hidden="true"></i> Texto exacto</span><i class="anticon anticon-down" aria-hidden="true"></i></summary>
                      <blockquote v-html="renderMarkdown(passage.quote)"></blockquote>
                    </details>
                    <p v-if="copyPassageErrorKey === sourcePassageKey(passage, passageIndex)" class="tool-workspace__passage-copy-error" role="status">No se pudo copiar. Selecciona el texto del pasaje para copiarlo manualmente.</p>
                  </article>
                </li>
              </ol>
            </div>
          </template>
        </div>
      </aside>
    </div>
  `,
};
